/* Run & Tell: Run Art Route Maker (sections/rt-run-art.liquid).
   Pick a spot and a shape (or type up to 12 characters, 2 lines); we fetch runnable streets from OpenStreetMap
   (Overpass API), fit the shape onto the street graph in a Web Worker, draw streets and route on a canvas
   (no map tiles), and offer a GPX 1.1 track and a 1080 x 1350 art card.
   Two parts in this file:
   1. RtRunArtEngine: pure geometry and routing, no DOM. It runs inside a Worker made from its own source
      (Blob URL, so it works from the Shopify CDN), or on the main thread with yields if Workers are blocked.
   2. The UI: one instance per [data-rt-ra] root. Copy comes from the [data-rt-ra-data] JSON block
      (Gal's content.json plus "ui" microcopy). Card and share code follow the other Run & Tell tools. */

/* =====================================================================================================
   1. ENGINE
   ===================================================================================================== */
function RtRunArtEngine(workerScope) {
  'use strict';
  var M_LAT = 110574, M_PER_MI = 1609.344;
  var DETOUR = 1.2;          /* route length / ideal length, first guess before routing */
  var K_STROKE = 30;         /* edge cost = len x (1 + k (d / h)^2) while drawing a stroke */
  var K_CONN = 12;           /* the same for connectors, d = distance to the nearest stroke of the shape */
  var CELL = 60;             /* spatial grid cell, meters */
  var MAX_RADIUS = 5200;     /* meters; bigger areas are "too big" (Overpass load, phone memory) */

  function mLon(lat) { return 111320 * Math.cos(lat * Math.PI / 180); }
  function project(o, lat, lon) { return [(lon - o.lon) * mLon(o.lat), (lat - o.lat) * M_LAT]; }
  function unproject(o, x, y) { return [o.lat + y / M_LAT, o.lon + x / mLon(o.lat)]; }
  function hyp(ax, ay, bx, by) { var dx = bx - ax, dy = by - ay; return Math.sqrt(dx * dx + dy * dy); }
  function segDist(px, py, ax, ay, bx, by) {
    var dx = bx - ax, dy = by - ay, l2 = dx * dx + dy * dy, t = 0;
    if (l2 > 0) { t = ((px - ax) * dx + (py - ay) * dy) / l2; t = t < 0 ? 0 : t > 1 ? 1 : t; }
    var qx = ax + t * dx - px, qy = ay + t * dy - py;
    return Math.sqrt(qx * qx + qy * qy);
  }
  function err(code, info) { var e = new Error(code); e.code = code; e.info = info || {}; return e; }

  /* ---------- font: single stroke, blocky so letters sit on street grids. Unit box, y up, height 1 ---------- */
  var FONT = {
    A: [.6, [[0, 0, 0, .7, .3, 1, .6, .7, .6, 0], [0, .45, .6, .45]]],
    B: [.6, [[0, .5, .45, .5, .45, 1, 0, 1, 0, 0, .6, 0, .6, .5, .45, .5]]],
    C: [.6, [[.6, 1, 0, 1, 0, 0, .6, 0]]],
    D: [.6, [[0, 0, 0, 1, .35, 1, .6, .75, .6, .25, .35, 0, 0, 0]]],
    E: [.6, [[.6, 1, 0, 1, 0, 0, .6, 0], [0, .5, .45, .5]]],
    F: [.6, [[.6, 1, 0, 1, 0, 0], [0, .5, .45, .5]]],
    G: [.6, [[.6, 1, 0, 1, 0, 0, .6, 0, .6, .5, .3, .5]]],
    H: [.6, [[0, 0, 0, 1], [.6, 0, .6, 1], [0, .5, .6, .5]]],
    I: [.4, [[0, 1, .4, 1], [.2, 1, .2, 0], [0, 0, .4, 0]]],
    J: [.6, [[.6, 1, .6, 0, 0, 0, 0, .35]]],
    K: [.6, [[0, 0, 0, 1], [.6, 1, .05, .5, .6, 0]]],
    L: [.55, [[0, 1, 0, 0, .55, 0]]],
    M: [.8, [[0, 0, 0, 1, .4, .45, .8, 1, .8, 0]]],
    N: [.6, [[0, 0, 0, 1, .6, 0, .6, 1]]],
    O: [.6, [[0, 0, 0, 1, .6, 1, .6, 0, 0, 0]]],
    P: [.6, [[0, 0, 0, 1, .6, 1, .6, .5, 0, .5]]],
    Q: [.7, [[0, 0, 0, 1, .6, 1, .6, 0, 0, 0], [.3, .3, .7, -.1]]],
    R: [.6, [[0, 0, 0, 1, .6, 1, .6, .5, .1, .5, .6, 0]]],
    S: [.6, [[.6, .85, .6, 1, 0, 1, 0, .5, .6, .5, .6, 0, 0, 0, 0, .15]]],
    T: [.6, [[0, 1, .6, 1], [.3, 1, .3, 0]]],
    U: [.6, [[0, 1, 0, 0, .6, 0, .6, 1]]],
    V: [.6, [[0, 1, .3, 0, .6, 1]]],
    W: [.8, [[0, 1, 0, 0, .4, .55, .8, 0, .8, 1]]],
    X: [.6, [[0, 1, .6, 0], [0, 0, .6, 1]]],
    Y: [.6, [[0, 1, 0, .5, .6, .5, .6, 1], [.3, .5, .3, 0]]],
    Z: [.6, [[0, 1, .6, 1, 0, 0, .6, 0]]],
    0: [.55, [[0, 0, 0, 1, .55, 1, .55, 0, 0, 0, .55, 1]]],
    1: [.5, [[.05, .75, .25, 1, .25, 0], [0, 0, .5, 0]]],
    2: [.6, [[0, 1, .6, 1, .6, .5, 0, .5, 0, 0, .6, 0]]],
    3: [.6, [[0, 1, .6, 1, .6, 0, 0, 0], [.15, .5, .6, .5]]],
    4: [.6, [[0, 1, 0, .45, .6, .45], [.45, 1, .45, 0]]],
    5: [.6, [[.6, 1, 0, 1, 0, .55, .6, .55, .6, 0, 0, 0]]],
    6: [.6, [[.6, 1, 0, 1, 0, 0, .6, 0, .6, .5, 0, .5]]],
    7: [.6, [[0, 1, .6, 1, .25, 0]]],
    8: [.6, [[0, .5, 0, 1, .6, 1, .6, 0, 0, 0, 0, .5, .6, .5]]],
    9: [.6, [[.6, .5, 0, .5, 0, 1, .6, 1, .6, 0, 0, 0]]],
    '.': [.2, [[0, 0, 0, .2, .2, .2, .2, 0, 0, 0]]],
    '&': [.6, [[.6, 0, .1, .6, .1, 1, .45, 1, .45, .65, 0, .35, 0, 0, .35, 0, .6, .3]]]
  };
  var LETTER_GAP = .3, SPACE = .5;

  /* ---------- gallery shapes: one continuous polyline each (spurs are out and back) ---------- */
  function arc(p, cx, cy, rx, ry, a0, a1, n) {
    for (var i = 0; i <= n; i++) { var a = (a0 + (a1 - a0) * i / n) * Math.PI / 180; p.push(cx + rx * Math.cos(a), cy + ry * Math.sin(a)); }
    return p;
  }
  var GALLERY = {
    turkey: function () {
      var p = [.30, .40, .40, .20, .50, .10, .50, -.06, .50, .10, .64, .08, .64, -.08, .64, .08, .78, .14];
      var cx = .62, cy = .44, a0 = -25, step = 29;
      for (var i = 0; i < 5; i++) {
        var s = a0 + i * step, e = s + step, m = (s + e) / 2;
        [[s, .40], [s + 5, .53], [m, .62], [e - 5, .53], [e, .40]].forEach(function (q, j) {
          if (i > 0 && j === 0) return;
          var a = q[0] * Math.PI / 180; p.push(cx + q[1] * Math.cos(a), cy + q[1] * Math.sin(a));
        });
      }
      p.push(.36, .80, .32, .92, .20, .92, .14, .84, .03, .77, .14, .73, .15, .58, .22, .64, .30, .58, .30, .40);
      return p;
    },
    tree: function () {
      return [.44, 0, .44, .12, .08, .12, .33, .40, .17, .40, .38, .66, .25, .66, .5, 1, .75, .66, .62, .66, .83, .40, .67, .40, .92, .12, .56, .12, .56, 0, .44, 0];
    },
    heart: function () {
      var p = [];
      for (var i = 0; i <= 32; i++) {
        var t = Math.PI * 2 * i / 32, s = Math.sin(t);
        p.push(16 * s * s * s, 13 * Math.cos(t) - 5 * Math.cos(2 * t) - 2 * Math.cos(3 * t) - Math.cos(4 * t));
      }
      return p;
    },
    star: function () {
      var p = [];
      for (var i = 0; i <= 10; i++) { var r = i % 2 ? .2 : .5, a = (90 + 36 * i) * Math.PI / 180; p.push(.5 + r * Math.cos(a), .5 + r * Math.sin(a)); }
      return p;
    },
    snowflake: function () {
      var p = [0, 0];
      for (var k = 0; k < 6; k++) {
        var a = (90 + 60 * k) * Math.PI / 180, ux = Math.cos(a), uy = Math.sin(a);
        var bx = .3 * ux, by = .3 * uy;
        var l = a + Math.PI / 4, r = a - Math.PI / 4;
        p.push(bx, by, bx + .16 * Math.cos(l), by + .16 * Math.sin(l), bx, by, bx + .16 * Math.cos(r), by + .16 * Math.sin(r), bx, by, .5 * ux, .5 * uy, 0, 0);
      }
      return p;
    },
    candy_cane: function () {
      var p = [.48, 0, .62, 0, .62, .7];
      arc(p, .32, .7, .3, .3, 0, 180, 10);
      p.push(.02, .56, .16, .56, .16, .7);
      arc(p, .32, .7, .16, .16, 180, 0, 8);
      p.push(.48, 0);
      return p;
    },
    pumpkin: function () {
      var p = [.5, .82, .46, .82, .44, 1, .58, 1.02, .56, .82];
      p.push(.66, .62, .68, .40, .62, .16, .56, .02, .62, .16, .68, .40, .66, .62, .56, .82);
      arc(p, .5, .42, .5, .41, 75, -255, 26);
      p.push(.44, .82, .34, .62, .32, .40, .38, .16, .44, .02, .38, .16, .32, .40, .34, .62, .44, .82, .5, .82);
      return p;
    },
    shoe: function () {
      return [0, .04, .5, .04, 1, .04, 1.04, .14, 1, .28, .86, .34, .7, .42, .58, .52, .47, .66, .38, .72, .33, .6, .2, .62, .06, .7, 0, .6, 0, .04];
    },
    /* added in round 3 (Tal's gallery). A generator may also return a list of strokes; the gaps are run as lighter connectors */
    gingerbread: function () {
      var p = [.5, .30, .38, 0, .18, 0, .28, .44, .26, .56, 0, .56, 0, .72, .38, .74];
      arc(p, .5, .88, .16, .16, -130, 310, 14);
      p.push(.62, .74, 1, .72, 1, .56, .74, .56, .72, .44, .82, 0, .62, 0, .5, .30);
      return p;
    },
    ghost: function () {
      var p = [0, 0, 0, .55];
      arc(p, .4, .55, .4, .45, 180, 0, 12);
      p.push(.8, 0, .67, .14, .53, 0, .4, .14, .27, 0, .13, .14, 0, 0);
      return p;
    },
    bat: function () {
      var r = [0, 0, .18, .3, .34, .16, .5, .46, .66, .36, .8, .62, 1, 1, .66, .88, .36, .8, .14, .7, .11, .95, .05, .76];
      var p = r.slice();
      for (var i = r.length - 2; i >= 2; i -= 2) p.push(-r[i], r[i + 1]);
      p.push(0, 0);
      return p;
    },
    medal: function () {
      var p = [];
      arc(p, .5, .32, .32, .32, 65, -245, 22);
      p.push(.36, .62, .14, 1, .36, 1, .5, .76, .64, 1, .86, 1, .64, .62, .5 + .32 * Math.cos(65 * Math.PI / 180), .32 + .32 * Math.sin(65 * Math.PI / 180));
      return p;
    },
    stopwatch: function () {
      var p = [];
      arc(p, .5, .42, .42, .42, 79, -259, 26);
      p.push(.42, .9, .32, .9, .32, 1, .68, 1, .68, .9, .58, .9, .5 + .42 * Math.cos(79 * Math.PI / 180), .42 + .42 * Math.sin(79 * Math.PI / 180));
      return [p, [.5, .42, .68, .6]];
    },
    lightning: function () {
      return [.62, 1, .12, .44, .44, .44, .3, 0, .88, .6, .56, .6, .82, 1, .62, 1];
    },
    dinosaur: function () {
      return [0, .34, .28, .52, .52, .58, .66, .56, .74, .86, .80, .96, .96, .96, 1, .86, .84, .82, .84, .52, .82, .30, .82, 0, .70, 0, .68, .24, .44, .24, .40, 0, .28, 0, .28, .28, .18, .32, 0, .34];
    },
    unicorn: function () {
      return [.2, 0, .1, .2, .18, .28, .08, .44, .2, .52, .14, .66, .32, .76, .36, .9, .44, .78, .52, .8, .78, 1.06, .62, .74, .92, .46, .94, .34, .84, .28, .62, .34, .54, .16, .58, 0, .2, 0];
    },
    cat: function () {
      return [.18, 0, 0, .2, 0, .66, .06, 1, .34, .76, .66, .76, .94, 1, 1, .66, 1, .2, .82, 0, .18, 0];
    },
    dog: function () {
      return [.16, .56, .64, .56, .72, .80, .80, .90, .86, .84, 1, .80, 1, .68, .82, .66, .80, .42, .80, 0, .68, 0, .66, .30, .34, .30, .32, 0, .20, 0, .18, .46, 0, .80, .16, .56];
    },
    coffee: function () {
      return [.18, 0, .08, .72, .36, .72, .30, .82, .40, .92, .34, 1.02, .40, .92, .30, .82, .36, .72, .72, .72, .70, .60, .92, .60, .96, .46, .90, .30, .68, .28, .62, 0, .18, 0];
    },
    pizza: function () {
      var p = [.5, 0];
      arc(p, .5, .6, .53, .4, 160, 20, 10);
      p.push(.5, 0);
      return p;
    },
    smiley: function () {
      var face = [];
      arc(face, .5, .5, .5, .5, 60, 420, 28);
      var smile = [];
      arc(smile, .5, .5, .3, .3, 205, 335, 8);
      return [[.32, .86, .32, .6], smile, [.68, .6, .68, .86], face];
    },
    thumbsup: function () {
      return [0, 0, 0, .56, .26, .58, .38, .74, .40, 1, .56, 1, .60, .66, .94, .64, 1, .54, .92, .46, 1, .38, .92, .30, .98, .20, .88, 0, 0, 0];
    }
,
    /* added in v2 (50 shape gallery) */
    shamrock: function () {
      var p = [];
      for (var i = 0; i <= 54; i++) { var a = (-90 + 360 * i / 54) * Math.PI / 180, r = .3 + .22 * Math.cos(3 * (a - Math.PI / 2)); p.push(r * Math.cos(a), r * Math.sin(a)); }
      p.push(.08, -.55, p[0], p[1]); /* stem out and back */
      return p;
    },
    bunny: function () {
      var r = .34, p = [], ear = function (s) { return [s * .1, .33, s * .2, .62, s * .24, .9, s * .17, 1.02, s * .1, .92, s * .06, .62, s * .03, .345]; };
      arc(p, 0, 0, r, r, 72, -252, 30); /* around the head, right ear base to left ear base */
      var L = ear(-1), R = ear(1), out = [];
      /* left ear (outside up, inside down), across the top of the head, right ear (inside up, outside down) */
      out = p.concat(L, [0, .345], revPts(R));
      out.push(p[0], p[1]);
      return out;
    },
    egg: function () {
      var p = [];
      for (var i = 0; i <= 36; i++) { var t = 2 * Math.PI * i / 36 - Math.PI / 2, y = Math.sin(t); p.push(.38 * Math.cos(t) * (1 - .12 * y), .5 * y * (y > 0 ? 1.12 : .9)); }
      return p;
    },
    flag: function () {
      var p = [0, 0, 0, 1];
      for (var i = 0; i <= 8; i++) p.push(.9 * i / 8, 1 - .05 * Math.sin(Math.PI * 2 * i / 8));
      for (i = 8; i >= 0; i--) p.push(.9 * i / 8, .55 - .05 * Math.sin(Math.PI * 2 * i / 8));
      p.push(0, 0);
      return p;
    },
    cake: function () {
      return [0, 0, 1, 0, 1, .38, .84, .38, .84, .64, .55, .64, .55, .86, .5, .98, .45, .86, .45, .64, .16, .64, .16, .38, 0, .38, 0, 0];
    },
    snowman: function () {
      var p = [];
      arc(p, 0, .3, .3, .3, -90, 52, 10);
      arc(p, 0, .72, .22, .22, -42, 50, 6);
      arc(p, 0, 1.06, .15, .15, -45, 225, 14);
      arc(p, 0, .72, .22, .22, 130, 222, 6);
      arc(p, 0, .3, .3, .3, 128, 270, 10);
      return p;
    },
    stocking: function () {
      return [.28, 1, .72, 1, .72, .86, .72, .42, .9, .3, .98, .16, .9, .04, .7, 0, .42, .04, .22, .2, .26, .42, .28, .86, .28, 1, .28, .86, .72, .86];
    },
    trophy: function () {
      return [.27, 0, .73, 0, .73, .1, .57, .14, .56, .36, .7, .44, .79, .62, .97, .7, .97, .9, .8, .93, .8, 1, .2, 1, .2, .93, .03, .9, .03, .7, .21, .62, .3, .44, .44, .36, .43, .14, .27, .1, .27, 0];
    },
    footprint: function () {
      var sole = [];
      arc(sole, .5, .25, .22, .25, -90, 270, 22);
      var toes = [[.34, .78, .09], [.52, .84, .08], [.67, .78, .07], [.77, .67, .06]].map(function (t) { var c = []; arc(c, t[0], t[1], t[2], t[2], -90, 270, 8); return c; });
      return [sole].concat(toes);
    },
    mountain: function () {
      return [0, 0, .33, .7, .45, .5, .64, .95, 1, 0, 0, 0];
    },
    runner: function () {
      var head = [];
      arc(head, .6, .9, .1, .1, -110, 250, 10);
      return [.74, 0, .62, .22, .44, .42, .3, .2, .12, .24, .3, .2, .44, .42, .52, .7, .3, .62, .22, .48, .3, .62, .52, .7, .74, .58, .84, .7, .74, .58, .52, .7, .57, .8].concat(head.slice(0, -2), [.57, .8]);
    },
    fish: function () {
      var p = [0, .5];
      arc(p, .4, .5, .4, .32, 180, 25, 10);
      p.push(1, .85, .96, .5, 1, .15);
      arc(p, .4, .5, .4, .32, -25, -180, 10);
      return p;
    },
    whale: function () {
      return [0, .42, .06, .66, .26, .8, .3, 1, .26, .8, .5, .76, .7, .6, .82, .54, .92, .76, 1, .9, .98, .66, .9, .48, .76, .3, .5, .2, .2, .22, .05, .3, 0, .42];
    },
    butterfly: function () {
      return [0, .5, .22, .95, .48, .92, .42, .62, 0, .5, .36, .36, .3, .05, .08, .18, 0, .5, -.08, .18, -.3, .05, -.36, .36, 0, .5, -.42, .62, -.48, .92, -.22, .95, 0, .5];
    },
    house: function () {
      return [0, 0, 0, .58, .5, 1, 1, .58, 1, 0, .62, 0, .62, .36, .38, .36, .38, 0, 0, 0];
    },
    rocket: function () {
      return [.36, .2, .36, .68, .5, 1, .64, .68, .64, .2, .84, 0, .64, .08, .36, .08, .16, 0, .36, .2];
    },
    moon: function () {
      var p = [], a0 = 50, c = .25, ex = .5 * Math.cos(a0 * Math.PI / 180), ey = .5 * Math.sin(a0 * Math.PI / 180), ri = Math.sqrt((ex - c) * (ex - c) + ey * ey), b = Math.atan2(ey, ex - c) * 180 / Math.PI;
      arc(p, 0, 0, .5, .5, a0, 360 - a0, 18);
      arc(p, c, 0, ri, ri, 360 - b, b, 14);
      return p;
    },
    crown: function () {
      return [0, 0, 1, 0, 1, .72, .8, .48, .66, .9, .5, .56, .34, .9, .2, .48, 0, .72, 0, 0];
    },
    diamond: function () {
      return [0, .62, .5, 0, 1, .62, 0, .62, .22, .88, .78, .88, 1, .62];
    },
    icecream: function () {
      var p = [.5, 0, .76, .56];
      arc(p, .5, .66, .29, .29, -20, 200, 12);
      p.push(.24, .56, .5, 0);
      return p;
    },
    balloon: function () {
      var p = [.5, 0, .52, .2, .44, .27];
      arc(p, .5, .62, .3, .36, -100, 260, 22);
      p.push(.56, .27, .52, .2);
      return p;
    },
    umbrella: function () {
      var c = [1, .5];
      arc(c, .5, .5, .5, .5, 0, 180, 12);
      for (var i = 0; i < 4; i++) arc(c, .125 + .25 * i, .5, .125, .07, 180, 0, 4);
      return [c, [.5, .5, .5, .08, .44, 0, .36, .04, .34, .12]];
    },
    sun: function () {
      var p = [];
      for (var i = 0; i <= 16; i++) { var a = (90 + 22.5 * i) * Math.PI / 180, r = i % 2 ? .3 : .5; p.push(r * Math.cos(a), r * Math.sin(a)); }
      return p;
    },
    tulip: function () {
      return [.5, 0, .5, .5, .28, .58, .26, 1, .4, .8, .5, 1, .6, .8, .74, 1, .72, .58, .5, .5, .5, .3, .78, .4, .5, .2];
    }
  };
  var GALLERY_TEXT = { t262: '26.2', t131: '13.1', t5k: '5K', pr: 'PR' };

  function plen(p) { var s = 0; for (var i = 2; i < p.length; i += 2) s += hyp(p[i - 2], p[i - 1], p[i], p[i + 1]); return s; }
  function isClosed(p) { return p.length > 4 && Math.abs(p[0] - p[p.length - 2]) < 1e-9 && Math.abs(p[1] - p[p.length - 1]) < 1e-9; }
  function revPts(p) { var o = []; for (var i = p.length - 2; i >= 0; i -= 2) o.push(p[i], p[i + 1]); return o; }
  function rotStart(p, k) { /* closed polyline, start at vertex k */
    var n = p.length / 2 - 1, o = [];
    for (var i = 0; i <= n; i++) { var j = (k + i) % n; o.push(p[2 * j], p[2 * j + 1]); }
    return o;
  }
  /* every way a stroke can be drawn: both directions, any start vertex if closed */
  function variants(p) {
    var out = [p, revPts(p)];
    if (isClosed(p)) { for (var k = 1; k < p.length / 2 - 1; k++) { var q = rotStart(p, k); out.push(q, revPts(q)); } }
    return out;
  }
  function permutations(a) {
    if (a.length <= 1) return [a.slice()];
    var out = [];
    a.forEach(function (x, i) { permutations(a.slice(0, i).concat(a.slice(i + 1))).forEach(function (r) { out.push([x].concat(r)); }); });
    return out;
  }

  function cleanText(t) {
    return String(t || '').toUpperCase().replace(/♥|❤️?/g, '').replace(/\s+/g, ' ').trim();
  }
  var TEXT_MAX = 12, LINE_MAX = 6, LINE_GAP = .55;
  function textOk(t) { var c = cleanText(t); return c.length > 0 && c.replace(/ /g, '').length <= TEXT_MAX && /^[A-Z0-9.& ]+$/.test(c); }
  function lineW(t) { var x = 0, n = 0; for (var i = 0; i < t.length; i++) { if (t[i] === ' ') { x += SPACE; continue; } x += FONT[t[i]][0] + (n++ ? LETTER_GAP : 0); } return x; }
  /* up to 6 characters stay on one line; longer text wraps to 2 lines: at the space that gives the most even
     lines, or (one long word) in the middle */
  function textLines(t) {
    t = cleanText(t);
    if (t.replace(/ /g, '').length <= LINE_MAX) return [t];
    var best = null;
    for (var i = 1; i < t.length; i++) {
      var a, b, sp = t[i] === ' ';
      if (sp) { a = t.slice(0, i).trim(); b = t.slice(i + 1).trim(); }
      else if (t[i - 1] !== ' ') { a = t.slice(0, i); b = t.slice(i); } else continue;
      if (!a || !b) continue;
      /* a split inside a word costs as much as one letter of imbalance more than a split at a space */
      var cost = Math.max(lineW(a), lineW(b)) + (sp ? 0 : .9) + (!sp && t.indexOf(' ') >= 0 ? 2 : 0);
      if (!best || cost < best.cost - 1e-9) best = { cost: cost, lines: [a, b] };
    }
    return best ? best.lines : [t];
  }

  /* template: strokes in unit coords centered on 0,0, height 1, in drawing order */
  function template(spec) {
    var strokes = [], isText = false, label = spec.label || '';
    var text = spec.kind === 'text' ? cleanText(spec.text) : GALLERY_TEXT[spec.id];
    var lines = 1, sg = [], glyphs = [];
    if (text != null) {
      if (!textOk(text)) throw err('bad_text');
      isText = true;
      var tl = textLines(text), wMax = Math.max.apply(null, tl.map(lineW));
      lines = tl.length;
      tl.forEach(function (line, li) {
        /* lines centered; the second line runs right to left, so the pen moves down at the right end (short connector) */
        var y0 = (tl.length - 1 - li) * (1 + LINE_GAP), x = (wMax - lineW(line)) / 2, row = [];
        for (var i = 0; i < line.length; i++) {
          var ch = line[i];
          if (ch === ' ') { x += SPACE; continue; }
          var g = FONT[ch], gx = x;
          row.push({ x: gx, y: y0, w: g[0], rtl: li > 0, line: li, s: g[1].map(function (st) { var o = []; for (var j = 0; j < st.length; j += 2) o.push(st[j] + gx, st[j + 1] + y0); return o; }) });
          x += g[0] + LETTER_GAP;
        }
        if (li > 0) row.reverse();
        glyphs = glyphs.concat(row);
      });
      var anchor = function (g) { return [g.rtl ? g.x + g.w : g.x, g.y]; };
      var cur = anchor(glyphs[0]);
      glyphs.forEach(function (g, gi) {
        var next = glyphs[gi + 1] ? anchor(glyphs[gi + 1]) : null;
        var best = null;
        permutations(g.s.map(function (_, k) { return k; })).forEach(function (perm) {
          (function walk(k, pos, acc, chosen) {
            if (best && acc >= best.cost) return;
            if (k === perm.length) {
              var c = acc + (next ? .5 * hyp(pos[0], pos[1], next[0], next[1]) : 0);
              if (!best || c < best.cost) best = { cost: c, list: chosen.slice() };
              return;
            }
            variants(g.s[perm[k]]).forEach(function (v) {
              var d = hyp(pos[0], pos[1], v[0], v[1]) * (gi === 0 && k === 0 ? .3 : 1);
              chosen.push(v); walk(k + 1, [v[v.length - 2], v[v.length - 1]], acc + d, chosen); chosen.pop();
            });
          })(0, cur, 0, []);
        });
        best.list.forEach(function (v) { strokes.push(v); sg.push(gi); });
        var last = strokes[strokes.length - 1];
        cur = [last[last.length - 2], last[last.length - 1]];
      });
    } else {
      var gen = GALLERY[spec.id];
      if (!gen) throw err('bad_shape');
      var p = gen();
      if (Array.isArray(p[0])) strokes = p;
      else {
        /* start at the lowest vertex of a closed shape, so the start pin sits at the bottom */
        if (isClosed(p)) { var lo = 0; for (var q = 0; q < p.length / 2 - 1; q++) if (p[2 * q + 1] < p[2 * lo + 1] - 1e-9) lo = q; p = rotStart(p, lo); }
        strokes = [p];
      }
    }
    /* normalize: height 1, centered */
    var minx = Infinity, maxx = -Infinity, miny = Infinity, maxy = -Infinity;
    strokes.forEach(function (s) { for (var i = 0; i < s.length; i += 2) { minx = Math.min(minx, s[i]); maxx = Math.max(maxx, s[i]); miny = Math.min(miny, s[i + 1]); maxy = Math.max(maxy, s[i + 1]); } });
    var H = isText ? 1 : (maxy - miny), cx = (minx + maxx) / 2, cy = isText && lines < 2 ? .5 : (miny + maxy) / 2;
    strokes = strokes.map(function (s) { var o = []; for (var i = 0; i < s.length; i += 2) o.push((s[i] - cx) / H, (s[i + 1] - cy) / H); return o; });
    var strokeLen = 0, connLen = 0, wLen = 0;
    strokes.forEach(function (s, i) {
      strokeLen += plen(s);
      for (var q = 2; q < s.length; q += 2) wLen += Math.abs(s[q] - s[q - 2]);
      if (i) { var a = strokes[i - 1]; connLen += hyp(a[a.length - 2], a[a.length - 1], s[0], s[1]); }
    });
    /* segments the outline itself runs out and back (turkey legs, snowflake arms): the score allows retracing there */
    var segK = {}, kf = function (x1, y1, x2, y2) { return [x1, y1, x2, y2].map(function (v) { return Math.round(v * 1e4); }).join(','); };
    strokes.forEach(function (s) { for (var i = 2; i < s.length; i += 2) segK[kf(s[i - 2], s[i - 1], s[i], s[i + 1])] = 1; });
    var twin = strokes.map(function (s) { var t = []; for (var i = 2; i < s.length; i += 2) t.push(segK[kf(s[i], s[i + 1], s[i - 2], s[i - 1])] ? 1 : 0); return t; });
    /* glyph boxes in the same units, for the per-letter fit (each letter can slide and widen a little to sit on streets) */
    var gl = glyphs.map(function (g) { return { x0: (g.x - cx) / H, x1: (g.x + g.w - cx) / H, line: g.line }; });
    return { twin: twin, strokes: strokes, sg: sg, glyphs: gl, isText: isText, lines: lines, label: label, w: (maxx - minx) / H, h: (maxy - miny) / H, strokeLen: strokeLen, connLen: connLen, wLen: wLen, idealLen: strokeLen + connLen };
  }

  /* first guess at the shape height (m) and the radius of street data it needs */
  function plan(spec, targetM) {
    var T = template(spec);
    var h0 = targetM / (T.idealLen * DETOUR);
    var half = .5 * Math.sqrt(T.w * T.w + T.h * T.h) * h0 * 1.15;
    var radius = half + .25 * h0 + 250;
    return { h0: h0, half: half, radius: radius, tooBig: radius > MAX_RADIUS, maxTargetM: targetM * (MAX_RADIUS - 250) / (radius - 250), w: T.w, hh: T.h };
  }

  /* ---------- street data ---------- */
  var HW_OK = /^(residential|unclassified|tertiary|tertiary_link|secondary|secondary_link|living_street|pedestrian|footway|path|cycleway|service|track|steps|road|bridleway)$/;
  function wayAllowed(t) {
    if (!t) return true; /* "out skel" has no tags: the Overpass query already filtered */
    var hw = t.highway || '';
    var footOk = /^(yes|designated|permissive)$/.test(t.foot || '');
    if (/^(motorway|motorway_link|trunk|trunk_link|construction|proposed|raceway|bus_guideway|platform|elevator)$/.test(hw)) return false;
    if (/^(no|private)$/.test(t.foot || '')) return false;
    if (/^(private|no)$/.test(t.access || '') && !footOk) return false;
    if (/^(parking_aisle|drive-through)$/.test(t.service || '')) return false;
    if (/^(primary|primary_link)$/.test(hw)) {
      var sw = [t.sidewalk, t['sidewalk:both'], t['sidewalk:left'], t['sidewalk:right']].filter(Boolean);
      return footOk || sw.some(function (v) { return !/^(no|none)$/.test(v); });
    }
    return HW_OK.test(hw) || (footOk && !!hw);
  }

  /* raw Overpass JSON (skel+nodes or geom) -> compact { o:[lat,lon], q:1e6, n:[dlat,dlon,...], wl:[len...], wi:[delta idx...] } */
  function compactFromOverpass(json, bbox) {
    var els = json && json.elements;
    if (!els) throw err('busy', { why: 'no elements' });
    var nodeLL = {}, ways = [];
    els.forEach(function (e) {
      if (e.type === 'node') nodeLL[e.id] = [e.lat, e.lon];
      else if (e.type === 'way' && e.nodes && wayAllowed(e.tags)) {
        if (e.geometry) e.geometry.forEach(function (g, i) { if (g) nodeLL[e.nodes[i]] = [g.lat, g.lon]; });
        ways.push(e.nodes);
      }
    });
    var idx = {}, lats = [], lons = [], wl = [], wi = [];
    ways.forEach(function (w) {
      var seq = [];
      w.forEach(function (id) {
        var ll = nodeLL[id];
        if (!ll) { if (seq.length > 1) push(seq); seq = []; return; }
        if (idx[id] == null) { idx[id] = lats.length; lats.push(ll[0]); lons.push(ll[1]); }
        seq.push(idx[id]);
      });
      if (seq.length > 1) push(seq);
    });
    function push(seq) { wl.push(seq.length); var prev = 0; seq.forEach(function (v) { wi.push(v - prev); prev = v; }); }
    var o;
    if (bbox) o = [(bbox[0] + bbox[2]) / 2, (bbox[1] + bbox[3]) / 2];
    else if (lats.length) {
      var a = Math.min.apply(null, lats), b = Math.max.apply(null, lats), c = Math.min.apply(null, lons), d = Math.max.apply(null, lons);
      o = [(a + b) / 2, (c + d) / 2];
    } else o = [0, 0];
    var n = [], pl = 0, po = 0;
    for (var i = 0; i < lats.length; i++) {
      var ql = Math.round(lats[i] * 1e6), qo = Math.round(lons[i] * 1e6);
      n.push(ql - pl, qo - po); pl = ql; po = qo;
    }
    return { v: 1, o: [Math.round(o[0] * 1e6) / 1e6, Math.round(o[1] * 1e6) / 1e6], n: n, wl: wl, wi: wi, bbox: bbox || null };
  }

  /* compact -> graph */
  function buildGraph(c) {
    var o = { lat: c.o[0], lon: c.o[1] };
    var N = c.n.length / 2, X = new Float64Array(N), Y = new Float64Array(N), LAT = new Float64Array(N), LON = new Float64Array(N);
    var pl = 0, po = 0, ml = mLon(o.lat);
    for (var i = 0; i < N; i++) {
      pl += c.n[2 * i]; po += c.n[2 * i + 1];
      LAT[i] = pl / 1e6; LON[i] = po / 1e6;
      X[i] = (LON[i] - o.lon) * ml; Y[i] = (LAT[i] - o.lat) * M_LAT;
    }
    var adj = new Array(N);
    for (i = 0; i < N; i++) adj[i] = [];
    var ways = [], k = 0;
    c.wl.forEach(function (len) {
      var seq = [], prev = 0;
      for (var j = 0; j < len; j++) { prev += c.wi[k++]; seq.push(prev); }
      ways.push(seq);
      for (j = 1; j < seq.length; j++) {
        var a = seq[j - 1], b = seq[j];
        if (a === b || adj[a].indexOf(b) >= 0) continue;
        adj[a].push(b); adj[b].push(a);
      }
    });
    var off = new Int32Array(N + 1), E = 0;
    for (i = 0; i < N; i++) { off[i] = E; E += adj[i].length; }
    off[N] = E;
    var nb = new Int32Array(E), el = new Float64Array(E);
    for (i = 0; i < N; i++) for (var q = 0; q < adj[i].length; q++) { var t = adj[i][q]; nb[off[i] + q] = t; el[off[i] + q] = hyp(X[i], Y[i], X[t], Y[t]); }
    /* components */
    var comp = new Int32Array(N).fill(-1), compSize = [], stack = [];
    for (i = 0; i < N; i++) {
      if (comp[i] >= 0 || off[i + 1] === off[i]) continue;
      var id = compSize.length, size = 0;
      comp[i] = id; stack.push(i);
      while (stack.length) {
        var u = stack.pop(); size++;
        for (var e = off[u]; e < off[u + 1]; e++) { var v = nb[e]; if (comp[v] < 0) { comp[v] = id; stack.push(v); } }
      }
      compSize.push(size);
    }
    /* bounds and grids */
    var minx = Infinity, miny = Infinity, maxx = -Infinity, maxy = -Infinity;
    for (i = 0; i < N; i++) { if (X[i] < minx) minx = X[i]; if (X[i] > maxx) maxx = X[i]; if (Y[i] < miny) miny = Y[i]; if (Y[i] > maxy) maxy = Y[i]; }
    if (!N) { minx = miny = maxx = maxy = 0; }
    var gw = Math.max(1, Math.ceil((maxx - minx) / CELL) + 1), gh = Math.max(1, Math.ceil((maxy - miny) / CELL) + 1);
    function cellOf(x, y) { var cx = Math.floor((x - minx) / CELL), cy = Math.floor((y - miny) / CELL); return [cx < 0 ? 0 : cx >= gw ? gw - 1 : cx, cy < 0 ? 0 : cy >= gh ? gh - 1 : cy]; }
    /* node grid */
    var ncount = new Int32Array(gw * gh + 1);
    for (i = 0; i < N; i++) { var cc = cellOf(X[i], Y[i]); ncount[cc[1] * gw + cc[0] + 1]++; }
    for (i = 1; i <= gw * gh; i++) ncount[i] += ncount[i - 1];
    var nfill = ncount.slice(), ncell = new Int32Array(N);
    for (i = 0; i < N; i++) { cc = cellOf(X[i], Y[i]); ncell[nfill[cc[1] * gw + cc[0]]++] = i; }
    /* edge grid (each undirected edge once) */
    var ea = [], eb = [];
    for (i = 0; i < N; i++) for (e = off[i]; e < off[i + 1]; e++) if (nb[e] > i) { ea.push(i); eb.push(nb[e]); }
    var ecount = new Int32Array(gw * gh + 1), cellsOf = [];
    for (q = 0; q < ea.length; q++) {
      var c0 = cellOf(Math.min(X[ea[q]], X[eb[q]]), Math.min(Y[ea[q]], Y[eb[q]])), c1 = cellOf(Math.max(X[ea[q]], X[eb[q]]), Math.max(Y[ea[q]], Y[eb[q]]));
      cellsOf.push([c0, c1]);
      for (var gy = c0[1]; gy <= c1[1]; gy++) for (var gx = c0[0]; gx <= c1[0]; gx++) ecount[gy * gw + gx + 1]++;
    }
    for (i = 1; i <= gw * gh; i++) ecount[i] += ecount[i - 1];
    var eux = new Float32Array(ea.length), euy = new Float32Array(ea.length);
    for (q = 0; q < ea.length; q++) { var L0 = hyp(X[ea[q]], Y[ea[q]], X[eb[q]], Y[eb[q]]) || 1; eux[q] = (X[eb[q]] - X[ea[q]]) / L0; euy[q] = (Y[eb[q]] - Y[ea[q]]) / L0; }
    var efill = ecount.slice(), ecell = new Int32Array(ecount[gw * gh]);
    for (q = 0; q < ea.length; q++) { c0 = cellsOf[q][0]; c1 = cellsOf[q][1]; for (gy = c0[1]; gy <= c1[1]; gy++) for (gx = c0[0]; gx <= c1[0]; gx++) ecell[efill[gy * gw + gx]++] = q; }
    return {
      o: o, N: N, X: X, Y: Y, LAT: LAT, LON: LON, off: off, nb: nb, el: el, comp: comp, compSize: compSize, ways: ways,
      minx: minx, miny: miny, maxx: maxx, maxy: maxy, gw: gw, gh: gh, cellOf: cellOf,
      ncount: ncount, ncell: ncell, ea: Int32Array.from(ea), eb: Int32Array.from(eb), eux: eux, euy: euy, ecount: ecount, ecell: ecell, bbox: c.bbox,
      stamp: new Int32Array(N), g: new Float64Array(N), prev: new Int32Array(N), closed: new Uint8Array(N), gen: 0,
      dcache: new Float64Array(N), dstamp: new Int32Array(N), dgen: 0
    };
  }

  /* nearest node, optionally only in component `comp`, preferring intersections (degree >= 3) within `prefR` */
  function nearestNode(G, x, y, comp, prefR) {
    var c = G.cellOf(x, y), best = -1, bd = Infinity, bestI = -1, bdI = Infinity;
    for (var r = 0; r < Math.max(G.gw, G.gh); r++) {
      for (var gy = c[1] - r; gy <= c[1] + r; gy++) {
        if (gy < 0 || gy >= G.gh) continue;
        for (var gx = c[0] - r; gx <= c[0] + r; gx++) {
          if (gx < 0 || gx >= G.gw || (Math.abs(gy - c[1]) !== r && Math.abs(gx - c[0]) !== r)) continue;
          var k = gy * G.gw + gx;
          for (var q = G.ncount[k]; q < G.ncount[k + 1]; q++) {
            var i = G.ncell[q];
            if (comp != null && G.comp[i] !== comp) continue;
            var d = hyp(x, y, G.X[i], G.Y[i]);
            if (d < bd) { bd = d; best = i; }
            if (prefR && G.off[i + 1] - G.off[i] >= 3 && d < bdI) { bdI = d; bestI = i; }
          }
        }
      }
      var reach = r * CELL;
      if (best >= 0 && reach > bd && (!prefR || reach > prefR)) break;
    }
    if (prefR && bestI >= 0 && bdI <= prefR) return { i: bestI, d: bdI };
    return { i: best, d: bd };
  }
  /* nearest intersection (or node) not in `used`, within the same component */
  function nearestFree(G, x, y, comp, used) {
    var c = G.cellOf(x, y), best = -1, bd = Infinity;
    for (var r = 0; r < 6; r++) {
      for (var gy = c[1] - r; gy <= c[1] + r; gy++) {
        if (gy < 0 || gy >= G.gh) continue;
        for (var gx = c[0] - r; gx <= c[0] + r; gx++) {
          if (gx < 0 || gx >= G.gw || (Math.abs(gy - c[1]) !== r && Math.abs(gx - c[0]) !== r)) continue;
          var k = gy * G.gw + gx;
          for (var q = G.ncount[k]; q < G.ncount[k + 1]; q++) {
            var i = G.ncell[q];
            if (used[i] || (comp != null && G.comp[i] !== comp)) continue;
            var d = hyp(x, y, G.X[i], G.Y[i]) * (G.off[i + 1] - G.off[i] >= 3 ? 1 : 1.6);
            if (d < bd) { bd = d; best = i; }
          }
        }
      }
      if (best >= 0 && r * CELL > bd) break;
    }
    return best >= 0 ? { i: best, d: bd } : nearestNode(G, x, y, comp, 0);
  }
  /* distance to the nearest street segment running roughly the same way (within ~30 degrees), capped */
  function streetDist(G, x, y, cap, ux, uy) {
    var c0 = G.cellOf(x - cap, y - cap), c1 = G.cellOf(x + cap, y + cap), best = cap;
    for (var gy = c0[1]; gy <= c1[1]; gy++) for (var gx = c0[0]; gx <= c1[0]; gx++) {
      var k = gy * G.gw + gx;
      for (var q = G.ecount[k]; q < G.ecount[k + 1]; q++) {
        var e = G.ecell[q];
        if (ux != null && Math.abs(G.eux[e] * ux + G.euy[e] * uy) < .94) continue;
        var a = G.ea[e], b = G.eb[e];
        var d = segDist(x, y, G.X[a], G.Y[a], G.X[b], G.Y[b]);
        if (d < best) best = d;
      }
    }
    return best;
  }

  /* street stats in a disc: intersections -> typical block spacing; dominant street bearing (mod 90) */
  function areaStats(G, cx, cy, r) {
    var c0 = G.cellOf(cx - r, cy - r), c1 = G.cellOf(cx + r, cy + r), nodes = 0, inter = 0, compCount = {};
    var hist = new Float64Array(90), total = 0;
    for (var gy = c0[1]; gy <= c1[1]; gy++) for (var gx = c0[0]; gx <= c1[0]; gx++) {
      var k = gy * G.gw + gx;
      for (var q = G.ncount[k]; q < G.ncount[k + 1]; q++) {
        var i = G.ncell[q];
        if (hyp(cx, cy, G.X[i], G.Y[i]) > r) continue;
        nodes++;
        if (G.comp[i] >= 0) compCount[G.comp[i]] = (compCount[G.comp[i]] || 0) + 1;
        var deg = G.off[i + 1] - G.off[i];
        if (deg >= 3) inter++;
        for (var e = G.off[i]; e < G.off[i + 1]; e++) {
          var j = G.nb[e]; if (j < i) continue;
          var ang = Math.atan2(G.Y[j] - G.Y[i], G.X[j] - G.X[i]) * 180 / Math.PI;
          var b = ((Math.round(ang) % 90) + 90) % 90;
          hist[b] += G.el[e]; total += G.el[e];
        }
      }
    }
    var comp = null, cmax = 0;
    Object.keys(compCount).forEach(function (c) { if (compCount[c] > cmax) { cmax = compCount[c]; comp = +c; } });
    var bestB = 0, bestW = 0;
    for (var b0 = 0; b0 < 90; b0++) { var w = 0; for (var d = -4; d <= 4; d++) w += hist[(b0 + d + 90) % 90]; if (w > bestW) { bestW = w; bestB = b0; } }
    var strength = total ? bestW / total : 0;
    var angle = bestB > 45 ? bestB - 90 : bestB;
    var area = Math.PI * r * r;
    return { nodes: nodes, inter: inter, spacing: inter ? Math.sqrt(area / inter) : Infinity, comp: comp, compNodes: cmax, gridAngle: strength > .3 ? angle : null, gridStrength: strength };
  }

  /* ---------- A* on the street graph ---------- */
  function astar(G, s, t, P) {
    if (s === t) return [s];
    var gen = ++G.gen, X = G.X, Y = G.Y, g = G.g, prev = G.prev, st = G.stamp, closed = G.closed;
    var heapN = [], heapK = [];
    function push(n, k) {
      var i = heapN.length; heapN.push(n); heapK.push(k);
      while (i > 0) { var p = (i - 1) >> 1; if (heapK[p] <= k) break; heapN[i] = heapN[p]; heapK[i] = heapK[p]; i = p; }
      heapN[i] = n; heapK[i] = k;
    }
    function pop() {
      var top = heapN[0], ln = heapN.pop(), lk = heapK.pop(), n = heapN.length;
      if (n) {
        var i = 0;
        while (true) { var l = 2 * i + 1, r = l + 1, m = i, mk = lk; if (l < n && heapK[l] < mk) { m = l; mk = heapK[l]; } if (r < n && heapK[r] < mk) { m = r; } if (m === i) break; heapN[i] = heapN[m]; heapK[i] = heapK[m]; i = m; }
        heapN[i] = ln; heapK[i] = lk;
      }
      return top;
    }
    var tx = X[t], ty = Y[t];
    st[s] = gen; g[s] = 0; prev[s] = -1; closed[s] = 0;
    push(s, hyp(X[s], Y[s], tx, ty));
    var mode = P.mode, ax = P.ax, ay = P.ay, bx = P.bx, by = P.by, k = P.k, ih2 = 1 / (P.h * P.h), segs = P.segs, expanded = 0;
    function dOutline(i) {
      if (G.dstamp[i] === G.dgen) return G.dcache[i];
      var best = Infinity;
      for (var q = 0; q < segs.length; q += 4) { var d = segDist(X[i], Y[i], segs[q], segs[q + 1], segs[q + 2], segs[q + 3]); if (d < best) best = d; }
      G.dstamp[i] = G.dgen; G.dcache[i] = best;
      return best;
    }
    while (heapN.length) {
      var u = pop();
      if (closed[u] && st[u] === gen) continue;
      closed[u] = 1;
      if (u === t) break;
      if (++expanded > 400000) return null;
      var gu = g[u];
      for (var e = G.off[u]; e < G.off[u + 1]; e++) {
        var v = G.nb[e], len = G.el[e], pen;
        if (mode === 1) {
          var du = segDist(X[u], Y[u], ax, ay, bx, by), dv = segDist(X[v], Y[v], ax, ay, bx, by), dm = segDist((X[u] + X[v]) / 2, (Y[u] + Y[v]) / 2, ax, ay, bx, by);
          pen = 1 + k * ((du * du + dv * dv + dm * dm) / 3) * ih2;
        } else if (mode === 2) {
          var o1 = dOutline(u), o2 = dOutline(v), oo = (o1 + o2) / 2;
          pen = 1 + k * oo * oo * ih2;
        } else pen = 1;
        var ng = gu + len * pen;
        if (st[v] !== gen) { st[v] = gen; closed[v] = 0; g[v] = Infinity; }
        if (closed[v]) continue;
        if (ng < g[v]) { g[v] = ng; prev[v] = u; push(v, ng + hyp(X[v], Y[v], tx, ty)); }
      }
    }
    if (st[t] !== gen || !closed[t]) return null;
    var path = [];
    for (var c = t; c !== -1; c = prev[c]) path.push(c);
    return path.reverse();
  }

  /* ---------- placing and routing one candidate ---------- */
  function place(T, cand, only) {
    var cs = Math.cos(cand.rot * Math.PI / 180), sn = Math.sin(cand.rot * Math.PI / 180), h = cand.h, gt = cand.gt, out = [];
    T.strokes.forEach(function (s, k) {
      var g = T.sg ? T.sg[k] : -1;
      if (only != null && g !== only) return;
      var t = gt && g >= 0 ? gt[g] : null, gb = t ? T.glyphs[g] : null, gc = gb ? (gb.x0 + gb.x1) / 2 : 0, o = [];
      for (var i = 0; i < s.length; i += 2) {
        var ux = t ? gc + t.dx + (s[i] - gc) * t.ws : s[i];
        var x = ux * h * (cand.sx || 1), y = s[i + 1] * h; o.push(cand.cx + x * cs - y * sn, cand.cy + x * sn + y * cs);
      }
      out.push(o);
    });
    return out;
  }
  /* per-letter fit for text: each letter may slide sideways and get a little wider or narrower so its strokes sit on
     streets (letter pitch rarely matches the block pitch). Left to right per line, keeping a gap between letters */
  var GLYPH_DX = [0, -.05, .05, -.1, .1, -.15, .15, -.2, .2], GLYPH_WS = [1, .8, 1.25, 1.5];
  function refineGlyphs(G, T, cand, step) {
    var n = T.glyphs.length, gt = [], i;
    for (i = 0; i < n; i++) gt.push({ dx: 0, ws: 1 });
    var c = { rot: cand.rot, h: cand.h, sx: cand.sx, cx: cand.cx, cy: cand.cy, gt: gt }, total = 0, cnt = 0;
    var order = T.glyphs.map(function (g, k) { return k; }).sort(function (a, b) { return T.glyphs[a].line - T.glyphs[b].line || T.glyphs[a].x0 - T.glyphs[b].x0; });
    var lastLine = -1, edge = -Infinity, minGap = .14;
    order.forEach(function (gi) {
      var gb = T.glyphs[gi], gc = (gb.x0 + gb.x1) / 2, hw = (gb.x1 - gb.x0) / 2;
      if (gb.line !== lastLine) { lastLine = gb.line; edge = -Infinity; }
      var best = null;
      GLYPH_WS.forEach(function (ws) {
        GLYPH_DX.forEach(function (dx) {
          var left = gc + dx - hw * ws;
          if (left < edge + minGap) return;
          gt[gi] = { dx: dx, ws: ws };
          var r = preParts(G, place(T, c, gi), cand.h, step);
          /* a small cost for each change, so a letter only moves or widens when it helps */
          var sc = r.n ? r.sum / r.n + .02 * Math.abs(dx) / .05 + .03 * Math.abs(Math.log(ws)) / .22 : 1;
          if (!best || sc < best.sc) best = { sc: sc, dx: dx, ws: ws, sum: r.sum, n: r.n };
        });
      });
      if (!best) best = { dx: 0, ws: 1, sum: 0, n: 0 };
      gt[gi] = { dx: best.dx, ws: best.ws };
      edge = gc + best.dx + hw * best.ws;
      total += best.sum; cnt += best.n;
    });
    return { gt: gt, pre: cnt ? total / cnt : 1 };
  }
  function samplePolyline(p, step) {
    var out = [p[0], p[1]];
    for (var i = 2; i < p.length; i += 2) {
      var ax = p[i - 2], ay = p[i - 1], bx = p[i], by = p[i + 1], L = hyp(ax, ay, bx, by), n = Math.max(1, Math.ceil(L / step));
      for (var j = 1; j <= n; j++) out.push(ax + (bx - ax) * j / n, ay + (by - ay) * j / n);
    }
    return out;
  }
  function prescore(G, T, cand, step) {
    var r = preParts(G, place(T, cand), cand.h, step);
    return r.n ? r.sum / r.n : 1;
  }
  function preParts(G, placed, h, step) {
    var cap = Math.min(.12 * h, 140), sum = 0, n = 0, st = step * h;
    placed.forEach(function (s) {
      for (var i = 2; i < s.length; i += 2) {
        var ax = s[i - 2], ay = s[i - 1], bx = s[i], by = s[i + 1], L = hyp(ax, ay, bx, by);
        if (L < 1e-6) continue;
        var ux = (bx - ax) / L, uy = (by - ay) / L, m = Math.max(1, Math.round(L / st));
        for (var j = 0; j < m; j++) { var t = (j + .5) / m, d = streetDist(G, ax + (bx - ax) * t, ay + (by - ay) * t, cap, ux, uy); sum += d * d; n++; }
      }
    });
    return { sum: sum / (cap * cap), n: n };
  }

  function collapseSpurs(path, protect) {
    var st = [];
    for (var i = 0; i < path.length; i++) {
      var v = path[i];
      if (st.length && st[st.length - 1] === v) continue;
      if (st.length >= 2 && st[st.length - 2] === v && !protect[st[st.length - 1]]) { st.pop(); continue; }
      st.push(v);
    }
    return st;
  }

  function routeCandidate(G, T, cand, comp) {
    var placed = place(T, cand), h = cand.h;
    var segs = [];
    placed.forEach(function (s) { for (var i = 2; i < s.length; i += 2) segs.push(s[i - 2], s[i - 1], s[i], s[i + 1]); });
    G.dgen++;
    var waySpacing = Math.max(T.strokeLen / 64, .045);
    var parts = [], far = 0, wpCount = 0, cur = -1;
    for (var si = 0; si < placed.length; si++) {
      var s = placed[si];
      /* waypoints: every vertex (snapped to an intersection when one is close) plus samples on long segments */
      var wps = [], protect = {};
      for (var i = 0; i < s.length; i += 2) {
        if (i) {
          var ax = s[i - 2], ay = s[i - 1], bx = s[i], by = s[i + 1], L = hyp(ax, ay, bx, by) / h, n = Math.floor(L / waySpacing);
          for (var j = 1; j < n; j++) wps.push({ x: ax + (bx - ax) * j / n, y: ay + (by - ay) * j / n, seg: i, v: false });
        }
        /* a vertex where the line doubles back (a spur tip, like a turkey leg) must keep its out-and-back */
        var tip = false;
        if (i > 0 && i < s.length - 2) {
          var ix = s[i] - s[i - 2], iy = s[i + 1] - s[i - 1], ox = s[i + 2] - s[i], oy = s[i + 3] - s[i + 1], li = hyp(0, 0, ix, iy), lo = hyp(0, 0, ox, oy);
          tip = li > 0 && lo > 0 && (ix * ox + iy * oy) / (li * lo) < -.5;
        }
        wps.push({ x: s[i], y: s[i + 1], seg: i, v: true, tip: tip });
      }
      /* a small closed loop (the dot in 26.2) snaps its corners to different nodes, so it stays a loop around a block
         instead of collapsing to one point or an out-and-back */
      var loop = isClosed(s) && plen(s) < 1.6 * h, used = {};
      wps.forEach(function (w, wi) {
        var nn = loop && w.v && wi < wps.length - 1 ? nearestFree(G, w.x, w.y, comp, used) : nearestNode(G, w.x, w.y, comp, w.v ? .1 * h : 0);
        if (loop && wi === wps.length - 1) nn = { i: wps[0].n, d: 0 };
        if (loop) { used[nn.i] = 1; if (w.v) protect[nn.i] = true; }
        w.n = nn.i; wpCount++;
        if (nn.d > .25 * h) far++;
        if (w.tip && nn.d < .12 * h) protect[nn.i] = true;
      });
      if (cur >= 0) {
        var cp = astar(G, cur, wps[0].n, { mode: 2, k: K_CONN, h: h, segs: segs });
        if (!cp) throw err('no_route');
        if (cp.length > 1) parts.push({ c: 1, nodes: cp });
      }
      var path = [wps[0].n];
      for (i = 1; i < wps.length; i++) {
        var a = wps[i - 1], b = wps[i];
        if (a.n === b.n) continue;
        /* the ideal line between these two waypoints */
        var p = astar(G, a.n, b.n, { mode: 1, k: K_STROKE, h: h, ax: a.x, ay: a.y, bx: b.x, by: b.y });
        if (!p) throw err('no_route');
        for (j = 1; j < p.length; j++) path.push(p[j]);
      }
      path = collapseSpurs(path, protect);
      parts.push({ c: 0, nodes: path });
      cur = path[path.length - 1];
    }
    var length = 0;
    parts.forEach(function (pt) { for (var q = 1; q < pt.nodes.length; q++) length += hyp(G.X[pt.nodes[q - 1]], G.Y[pt.nodes[q - 1]], G.X[pt.nodes[q]], G.Y[pt.nodes[q]]); });
    return { cand: cand, parts: parts, length: length, far: wpCount ? far / wpCount : 1, placed: placed, twin: T.twin, isText: T.isText };
  }

  /* shape match: F1 of "ideal near route" and "route near ideal", tolerance tau = 0.1 x shape height */
  /* shape match (v2, stricter): the mean of two F1 scores ("outline covered by the route" and "route lying on the
     outline") at 5% and 3.5% of the shape height, minus penalties for a missed feature (99th percentile distance of
     the outline from the route, like a turkey head that is not there), for long stray detours (the same from the
     route side), and for out-and-back stretches the shape itself does not have (a spike run as a dead end) */
  function shapeMatch(G, r) {
    /* text: h is the letter height, the small side of a word, so its tolerances are a notch wider */
    var h = r.cand.h * (r.isText ? 1.4 : 1), t1 = Math.max(.05 * h, 20), t2 = Math.max(.035 * h, 20), cap = .3 * h, step = Math.max(.0175 * h, 8);
    var route = [], strokesR = [], seen = {}, retr = 0, rlen = 0, N = G.N, ideal = r.placed;
    /* the outline's own out-and-back segments, placed */
    var twins = [];
    if (r.twin) ideal.forEach(function (s, si) { var tw = r.twin[si] || []; for (var i = 2; i < s.length; i += 2) if (tw[i / 2 - 1]) twins.push([s[i - 2], s[i - 1], s[i], s[i + 1]]); });
    r.parts.forEach(function (pt) {
      var sr = [];
      for (var q = 0; q < pt.nodes.length; q++) {
        var v = pt.nodes[q]; route.push(G.X[v], G.Y[v]); sr.push(G.X[v], G.Y[v]);
        if (!q || pt.c) continue;
        var u = pt.nodes[q - 1], L = hyp(G.X[u], G.Y[u], G.X[v], G.Y[v]), k = u < v ? u * N + v : v * N + u;
        rlen += L;
        if (!seen[k]) { seen[k] = 1; continue; }
        /* a retraced street counts against the score unless the outline goes out and back right there */
        var mx = (G.X[u] + G.X[v]) / 2, my = (G.Y[u] + G.Y[v]) / 2, ok = false;
        for (var w = 0; w < twins.length && !ok; w++) ok = segDist(mx, my, twins[w][0], twins[w][1], twins[w][2], twins[w][3]) <= Math.max(.08 * h, 25);
        if (!ok) retr += L;
      }
      if (!pt.c) strokesR.push(sr);
    });
    function minD(x, y, polys) {
      var b = cap;
      for (var a = 0; a < polys.length; a++) { var p = polys[a]; for (var i = 2; i < p.length; i += 2) { var d = segDist(x, y, p[i - 2], p[i - 1], p[i], p[i + 1]); if (d < b) b = d; } }
      return b;
    }
    var dI = [], dR = [];
    ideal.forEach(function (s) { var sp = samplePolyline(s, step); for (var i = 0; i < sp.length; i += 2) dI.push(minD(sp[i], sp[i + 1], [route])); });
    /* precision counts connectors too (Strava shows them); the stray-detour term looks at the drawn strokes only */
    var rs = samplePolyline(route, step), dS = [];
    for (var i = 0; i < rs.length; i += 2) dR.push(minD(rs[i], rs[i + 1], ideal));
    strokesR.forEach(function (p) { if (p.length < 4) return; var sp = samplePolyline(p, step); for (var i = 0; i < sp.length; i += 2) dS.push(minD(sp[i], sp[i + 1], ideal)); });
    function frac(a, t) { var n = 0; for (var i = 0; i < a.length; i++) if (a[i] <= t) n++; return a.length ? n / a.length : 0; }
    function f1(t) { var rc = frac(dI, t), pr = frac(dR, t); return rc + pr ? 2 * rc * pr / (rc + pr) : 0; }
    function q99(a) { var s = a.slice().sort(function (x, y) { return x - y; }); return s.length ? s[Math.floor(.99 * (s.length - 1))] / h : 0; }
    var recall = frac(dI, t1), precision = frac(dR, t1), hi = q99(dI), hr = q99(dS.length ? dS : dR), back = rlen ? retr / rlen : 0;
    var score = .5 * f1(t1) + .5 * f1(t2) - .4 * Math.max(0, hi - .08) - .25 * Math.max(0, hr - .1) - 1.5 * Math.max(0, back - .02);
    return { score: Math.max(0, Math.min(1, score)), recall: recall, precision: precision, tau: t1, miss: hi, stray: hr, back: back };
  }

  /* ---------- the fit: rotations x scales x offsets, cheap prescore, then route the best few ---------- */
  function wait() { return new Promise(function (r) { setTimeout(r, 0); }); }
  async function fit(G, opts, onStep) {
    var t0 = Date.now(), budget = opts.budgetMs || 3000;
    var T = template(opts.spec), target = opts.targetM;
    var cx = opts.x, cy = opts.y;
    var pl = plan(opts.spec, target);
    if (G.N < 8) throw err('no_streets');
    if (pl.tooBig) throw err('too_big', { maxMi: Math.floor(pl.maxTargetM / M_PER_MI * 2) / 2 });
    /* is the needed area inside the loaded data? (the demo areas and cached boxes are finite) */
    var needR = pl.half / 1.15 * 1.05 + 60;
    function covered(x, y) { return !(x - needR < G.minx - 50 || x + needR > G.maxx + 50 || y - needR < G.miny - 50 || y + needR > G.maxy + 50); }
    if (!covered(cx, cy)) {
      var room = Math.min(cx - G.minx, G.maxx - cx, cy - G.miny, G.maxy - cy) + 50;
      throw err('outside', { maxMi: room > 400 ? Math.floor(target * (room - 60) / (needR - 60) / M_PER_MI * 2) / 2 : 0 });
    }
    var h0 = pl.h0;
    /* block spacing measured in a fixed 1 km disc, so the "try N miles" advice does not move with the distance */
    var S1 = areaStats(G, cx, cy, 1000);
    var spacing = S1.spacing;
    if (!isFinite(spacing)) { var Sx = areaStats(G, cx, cy, Math.max(.5 * Math.max(T.w, T.h) * h0, 300)); spacing = Sx.spacing; }
    var minH = (T.isText ? 2.6 : 3.2) * spacing;
    if (isFinite(spacing) && h0 < minH) throw err('too_small', { needMi: Math.ceil(target * minH / h0 / M_PER_MI * 2) / 2, spacing: spacing });
    var ctx = { G: G, T: T, opts: opts, h0: h0, spacing: spacing, target: target, t0: t0, budget: budget, onStep: onStep };
    var pin = null, pinErr = null;
    try { pin = await fitAt(ctx, cx, cy, 1); }
    catch (e) { if (!opts.nearby || !/^(no_streets|off_map|no_route)$/.test(e.code || '')) throw e; pinErr = e; }
    var best = pin, moved = null, tried = 0;
    /* best spot nearby: rings of start centers around the pin, on the same street graph (no new download).
       A cheap prescore ranks them; the best three are routed for real and win only if clearly better. */
    if (opts.nearby) {
      var maxM = opts.nearby.maxM || 2400, ring = [];
      [[.33, 6], [.66, 8], [1, 10]].forEach(function (rg, k) {
        var d = rg[0] * maxM;
        for (var i = 0; i < rg[1]; i++) {
          var a = (i / rg[1] + k * .07) * 2 * Math.PI, x = cx + d * Math.cos(a), y = cy + d * Math.sin(a);
          if (covered(x, y)) ring.push({ x: x, y: y, d: d });
        }
      });
      var pinQ = pin ? quickScore(ctx, cx, cy) : Infinity;
      ring.forEach(function (c) { c.q = quickScore(ctx, c.x, c.y); });
      ring = ring.filter(function (c) { return c.q < pinQ * .92; }).sort(function (a, b) { return a.q - b.q; });
      var picks = [];
      var nPick = opts.nearby.picks || 3;
      ring.forEach(function (c) { if (picks.length < nPick && picks.every(function (p) { return hyp(p.x, p.y, c.x, c.y) > .3 * maxM; })) picks.push(c); });
      if (onStep) onStep('nearby', picks.length);
      for (var i = 0; i < picks.length; i++) {
        if (i && Date.now() - t0 > budget) break;
        var r = null;
        try { r = await fitAt(ctx, picks[i].x, picks[i].y, .7); } catch (e) { r = null; }
        tried++;
        if (!r) continue;
        /* moving costs the runner something: only for a clearly better fit (about 3 points of match or more) */
        if (!best || (r.out[0].match.score >= best.out[0].match.score + .03 && r.out[0].score < best.out[0].score - .02)) { best = r; moved = { dx: picks[i].x - cx, dy: picks[i].y - cy, d: hyp(picks[i].x, picks[i].y, cx, cy) }; }
      }
    }
    if (!best) throw pinErr || err('no_route');
    var out = best.out;
    if (out[0].far > .35) throw err('off_map', { far: out[0].far });
    return {
      debug: opts.debug ? { pre: best.cands.slice(0, 12).map(function (c) { return [c.rot, c.sx, +c.h.toFixed(0), c.off.join('/'), +c.pre.toFixed(3)]; }), routed: best.results.map(function (r) { return [r.cand.rot, r.cand.sx, Math.round(r.cand.h), r.cand.off.join('/'), Math.round(r.match.score * 100), +r.lenErr.toFixed(2), +r.score.toFixed(3)]; }) } : null,
      ms: Date.now() - t0, candidates: best.cands.length, routed: best.results.length, spacing: best.S.spacing, gridAngle: best.S.gridAngle, label: T.label, isText: T.isText,
      fits: out.map(function (r) { return exportFit(G, r); }),
      moved: moved ? { dx: Math.round(moved.dx), dy: Math.round(moved.dy), d: Math.round(moved.d) } : null,
      pinFits: moved && pin && pin.out[0].far <= .35 ? pin.out.map(function (r) { return exportFit(G, r); }) : null,
      nearbyTried: tried
    };
  }

  /* candidates for one center: rotations x scales x offsets (light = fewer, for the nearby rings) */
  function candidatesAt(ctx, cx, cy, S, light) {
    var T = ctx.T, h0 = ctx.h0, opts = ctx.opts;
    var rots = [-30, -20, -10, 0, 10, 20, 30];
    if (opts.align !== false && S.gridAngle != null) {
      var ga = S.gridAngle; if (rots.indexOf(ga) < 0) rots.push(ga);
    }
    if (opts.rots) rots = opts.rots;
    var s = Math.min(isFinite(ctx.spacing) ? ctx.spacing : 120, .2 * h0);
    var offs = light ? [[0, 0], [.5, 0], [0, .5], [.5, .5]] : [[0, 0], [.5, 0], [0, .5], [.5, .5], [-.25, .25], [1, 0, 1], [-1, 0, 1], [0, 1, 1], [0, -1, 1]];
    var cands = [];
    rots.forEach(function (rot) {
      var cs = Math.cos(rot * Math.PI / 180), sn = Math.sin(rot * Math.PI / 180);
      (light ? [1] : [.92, 1, 1.08]).forEach(function (sc) {
        (T.isText && !light ? [.8, 1, 1.25] : [1]).forEach(function (sx) {
          offs.forEach(function (of) {
            /* the first five are small nudges against the block grid; the last four slide the shape off the pin
               (about a fifth of its height) so it can dodge a park, water or a highway */
            var big = of[2] ? .22 * h0 / s : 1;
            var ox = of[0] * s * big, oy = of[1] * s * big;
            /* wider letters make a longer route: shrink the height to keep the distance */
            var hh = h0 * sc * (T.isText ? T.idealLen / (T.idealLen + (sx - 1) * T.wLen) : 1);
            cands.push({ rot: rot, h: hh, sx: sx, cx: cx + ox * cs - oy * sn, cy: cy + ox * sn + oy * cs, off: of });
          });
        });
      });
    });
    var step = Math.max(T.idealLen / (light ? 60 : 110), .03);
    cands.forEach(function (c) { c.pre = prescore(ctx.G, T, c, step) + (c.rot === S.gridAngle ? -.02 : 0) + Math.abs(c.rot) / 3000; });
    cands.sort(function (a, b) { return a.pre - b.pre; });
    return cands;
  }
  /* how promising a center looks before any routing (lower is better) */
  function quickScore(ctx, cx, cy) {
    var S = areaStats(ctx.G, cx, cy, Math.max(.5 * Math.max(ctx.T.w, ctx.T.h) * ctx.h0, 300));
    if (S.nodes < 8 || S.comp == null || S.compNodes < 8) return Infinity;
    return candidatesAt(ctx, cx, cy, S, true)[0].pre;
  }

  /* the full fit at one center: prescore, route the best few, correct the scale toward the distance */
  async function fitAt(ctx, cx, cy, budgetShare) {
    var G = ctx.G, T = ctx.T, h0 = ctx.h0, target = ctx.target, onStep = ctx.onStep, t0 = Date.now(), budget = ctx.budget * budgetShare;
    var S = areaStats(G, cx, cy, Math.max(.5 * Math.max(T.w, T.h) * h0, 300));
    if (S.nodes < 8 || S.comp == null || S.compNodes < 8) throw err('no_streets');
    var comp = S.comp;
    var cands = candidatesAt(ctx, cx, cy, S, false);
    if (T.isText && T.glyphs.length > 1) {
      /* per-letter fit on the most promising placements, then rank again */
      var stepG = Math.max(T.idealLen / 110, .03), seenG = {}, top = [];
      for (var q = 0; q < cands.length && top.length < 16; q++) { var kq = cands[q].rot + '|' + cands[q].sx + '|' + cands[q].off; if (seenG[kq]) continue; seenG[kq] = 1; top.push(cands[q]); }
      top.forEach(function (c) { var rg = refineGlyphs(G, T, c, stepG); c.gt = rg.gt; c.pre = rg.pre + (c.rot === S.gridAngle ? -.02 : 0) + Math.abs(c.rot) / 3000; });
      cands.sort(function (a, b) { return a.pre - b.pre; });
    }
    if (onStep) onStep('prescore', cands.length);
    /* route the best few, at most 1 per rotation and letter width */
    var pick = [], perRot = {};
    for (var i = 0; i < cands.length && pick.length < 5; i++) { var c = cands[i]; var rk = c.rot + '|' + c.sx; if ((perRot[rk] || 0) >= 1) continue; perRot[rk] = 1; pick.push(c); }
    var results = [];
    function evalR(r) {
      var m = shapeMatch(G, r);
      r.match = m;
      r.lenErr = Math.abs(r.length - target) / target;
      r.score = (1 - m.score) + 1.8 * r.lenErr + .3 * (r.cand.pre || 0) + (r.far > .2 ? r.far : 0);
      return r;
    }
    for (i = 0; i < pick.length; i++) {
      if (i >= 2 && Date.now() - t0 > budget * .6) break;
      results.push(evalR(routeCandidate(G, T, pick[i], comp)));
      if (onStep) onStep('route', i + 1);
      await wait();
    }
    results.sort(function (a, b) { return a.score - b.score; });
    /* scale correction toward the target distance for the best three */
    var top = results.slice(0, 3), more = [];
    for (i = 0; i < top.length; i++) {
      var r0 = top[i];
      if (r0.lenErr < .04 || (i > 0 && Date.now() - t0 > budget)) continue;
      for (var it = 0; it < 3; it++) {
        var nh = r0.cand.h * Math.pow(target / r0.length, .9);
        nh = Math.max(.5 * h0, Math.min(1.35 * h0, nh));
        var c2 = { rot: r0.cand.rot, h: nh, sx: r0.cand.sx, pre: r0.cand.pre, cx: r0.cand.cx, cy: r0.cand.cy, off: r0.cand.off, gt: r0.cand.gt };
        var r1 = evalR(routeCandidate(G, T, c2, comp));
        more.push(r1);
        await wait();
        if (r1.lenErr < .04) break;
        r0 = r1;
        if (Date.now() - t0 > budget * 1.2) break;
      }
    }
    results = results.concat(more).sort(function (a, b) { return a.score - b.score; });
    /* keep three different fits (different rotation or offset) */
    var out = [], seen = {};
    results.forEach(function (r) { var key = r.cand.rot + '|' + r.cand.off + '|' + r.cand.sx; if (out.length < 3 && !seen[key]) { seen[key] = 1; out.push(r); } });
    if (!out.length) throw err('no_route');
    if (out[0].far > .35) throw err('off_map', { far: out[0].far });
    return { out: out, results: results, cands: cands, S: S };
  }

  function exportFit(G, r) {
    var parts = r.parts.map(function (pt) {
      var o = nodesOut(G, pt.nodes); o.c = pt.c;
      return o;
    });
    return {
      parts: parts, lengthM: r.length, match: Math.round(r.match.score * 100), recall: r.match.recall, precision: r.match.precision, miss: r.match.miss, stray: r.match.stray, back: r.match.back,
      rot: r.cand.rot, sx: r.cand.sx || 1, h: r.cand.h, far: r.far, twin: r.twin, isText: !!r.isText, ideal: r.placed.map(function (s) { return s.map(function (v) { return Math.round(v * 10) / 10; }); })
    };
  }

  /* node list -> { n: node ids (for edits), xy: meters, ll: lat,lon } */
  function nodesOut(G, nodes) {
    var xy = [], ll = [];
    nodes.forEach(function (n) { xy.push(Math.round(G.X[n] * 10) / 10, Math.round(G.Y[n] * 10) / 10); ll.push(G.LAT[n], G.LON[n]); });
    return { n: nodes.slice(), xy: xy, ll: ll };
  }

  /* street polylines for drawing: flat xy + offsets */
  function streetsOf(G) {
    var total = 0; G.ways.forEach(function (w) { total += w.length; });
    var xy = new Float32Array(total * 2), offs = new Int32Array(G.ways.length + 1), k = 0;
    G.ways.forEach(function (w, i) { offs[i] = k; w.forEach(function (n) { xy[2 * k] = G.X[n]; xy[2 * k + 1] = G.Y[n]; k++; }); });
    offs[G.ways.length] = k;
    return { xy: xy, offs: offs };
  }

  /* ---------- API, used directly (main thread) or through postMessage (worker) ---------- */
  var graphs = {};
  var api = {
    template: template, plan: plan, textOk: textOk, cleanText: cleanText, textLines: textLines, TEXT_MAX: TEXT_MAX, project: project, unproject: unproject,
    compactFromOverpass: compactFromOverpass, buildGraph: buildGraph, wayAllowed: wayAllowed, M_PER_MI: M_PER_MI, FONT: FONT, GALLERY: GALLERY, GALLERY_TEXT: GALLERY_TEXT,
    /* payload: { text } raw Overpass, or { compact } */
    load: function (key, payload) {
      var compact = payload.compact;
      var made = false;
      if (!compact) {
        var json;
        try { json = JSON.parse(payload.text); } catch (e) { throw err('busy', { why: 'not json' }); }
        if (json.remark && /error|timed out|out of memory/i.test(json.remark) && !(json.elements && json.elements.length)) throw err('busy', { why: json.remark });
        compact = compactFromOverpass(json, payload.bbox);
        made = true;
      }
      var G = buildGraph(compact);
      graphs = {}; graphs[key] = G; /* keep one graph in memory */
      var st = streetsOf(G);
      return { key: key, nodes: G.N, ways: G.ways.length, origin: [G.o.lat, G.o.lon], bounds: [G.minx, G.miny, G.maxx, G.maxy], compact: made ? compact : null, streets: st };
    },
    fit: function (key, opts, onStep) {
      var G = graphs[key];
      if (!G) return Promise.reject(err('not_loaded'));
      return fit(G, opts, onStep);
    },
    /* drag to fix a detour: the shortest street path from node a through the street nearest (x, y) to node b */
    reroute: function (key, o) {
      var G = graphs[key];
      if (!G) throw err('not_loaded');
      var v = nearestNode(G, o.x, o.y, G.comp[o.a], 0).i;
      var P = { mode: 0, h: 1 }, p1 = v === o.a ? [v] : astar(G, o.a, v, P), p2 = v === o.b ? [v] : astar(G, v, o.b, P);
      if (!p1 || !p2) throw err('no_route');
      var out = nodesOut(G, p1.concat(p2.slice(1)));
      out.via = p1.length - 1;
      return out;
    },
    /* match and length of an edited route against the shape outline */
    rescore: function (key, o) {
      var G = graphs[key];
      if (!G) throw err('not_loaded');
      var parts = [{ nodes: o.nodes }];
      if (o.c && o.c.length === o.nodes.length - 1) { /* per-edge connector flags: score connectors as connectors, like the fit does */
        parts = [];
        for (var e = 0; e < o.c.length; e++) { var pc = parts[parts.length - 1]; if (!pc || pc.c !== (o.c[e] ? 1 : 0)) parts.push(pc = { nodes: [o.nodes[e]], c: o.c[e] ? 1 : 0 }); pc.nodes.push(o.nodes[e + 1]); }
      }
      var r = { cand: { h: o.h }, parts: parts, placed: o.ideal, twin: o.twin, isText: o.isText }, len = 0;
      for (var q = 1; q < o.nodes.length; q++) len += hyp(G.X[o.nodes[q - 1]], G.Y[o.nodes[q - 1]], G.X[o.nodes[q]], G.Y[o.nodes[q]]);
      var m = shapeMatch(G, r);
      return { match: Math.round(m.score * 100), recall: m.recall, precision: m.precision, lengthM: len };
    }
  };

  if (workerScope) {
    workerScope.onmessage = function (ev) {
      var m = ev.data, reply = function (ok, res) {
        var transfer = [];
        if (ok && res && res.streets) transfer = [res.streets.xy.buffer, res.streets.offs.buffer];
        workerScope.postMessage({ id: m.id, ok: ok, res: ok ? res : { code: res.code || 'error', info: res.info || {}, msg: String(res.message || res) } }, transfer);
      };
      try {
        if (m.cmd === 'ping') reply(true, 'pong');
        else if (m.cmd === 'load') reply(true, api.load(m.key, m.payload));
        else if (m.cmd === 'fit') api.fit(m.key, m.opts, function (phase, n) { workerScope.postMessage({ id: m.id, step: phase, n: n }); }).then(function (r) { reply(true, r); }, function (e) { reply(false, e); });
        else if (m.cmd === 'reroute' || m.cmd === 'rescore') reply(true, api[m.cmd](m.key, m.opts));
      } catch (e) { reply(false, e); }
    };
  }
  return api;
}

/* =====================================================================================================
   2. UI
   ===================================================================================================== */
(function () {
  'use strict';
  if (typeof document === 'undefined') { if (typeof module !== 'undefined') module.exports = RtRunArtEngine; return; }

  var CARD_W = 1080, CARD_H = 1350, CREDIT = 'runntell.com', FONT_TIMEOUT = 4000;
  var STORY_W = 1080, STORY_H = 1920, STORY_SAFE = 250;
  var COLORS = { bg: '#0F0BE3', ink: '#FFF9EC', sun: '#FFE135', street: '#CFCDE6', mapBg: '#FFFFFF', route: '#0F0BE3' };
  var FONTS = {
    big: { family: '"Sofia Sans Extra Condensed", "Arial Narrow", "Arial Black", sans-serif', weight: '900' },
    hand: { family: 'Gaegu, "Marker Felt", "Comic Sans MS", cursive', weight: '700' }
  };
  var LS_PREFIX = 'rt-ra-v1:', LS_KEEP = 2;
  var DEFAULT_OVERPASS = ['https://overpass-api.de/api/interpreter', 'https://overpass.kumi.systems/api/interpreter', 'https://overpass.private.coffee/api/interpreter'];
  var DEFAULT_NOMINATIM = 'https://nominatim.openstreetmap.org/search';
  var DEFAULT_PHOTON = 'https://photon.komoot.io/api/';

  function parseJSON(s) { try { return JSON.parse(s); } catch (e) { return null; } }
  function fontStr(f, size) { return f.weight + ' ' + Math.max(1, Math.round(size)) + 'px ' + f.family; }
  function withTimeout(p, ms) { return Promise.race([p, new Promise(function (r) { setTimeout(r, ms); })]); }
  function fill(tpl, vars) { return String(tpl == null ? '' : tpl).replace(/\{(\w+)\}/g, function (m, k) { return vars && vars[k] != null ? vars[k] : m; }); }
  function hand(t) { return String(t || '').replace(/’/g, "'"); }
  function fmtMiles(m) { return (Math.round(m * 10) / 10).toFixed(1); }
  function fmtTime(sec, ui) {
    var mins = Math.max(1, Math.round(sec / 60)), h = Math.floor(mins / 60), m = mins % 60;
    return h > 0 ? fill(ui.time_hm || '{h} hr {m} min', { h: h, m: m }) : fill(ui.time_m || '{m} min', { m: m });
  }
  function lsGet(k) { try { return window.localStorage.getItem(k); } catch (e) { return null; } }
  function lsSet(k, v) { try { window.localStorage.setItem(k, v); return true; } catch (e) { return false; } }
  function lsDel(k) { try { window.localStorage.removeItem(k); } catch (e) {} }
  function lsKeys() { var out = []; try { for (var i = 0; i < localStorage.length; i++) { var k = localStorage.key(i); if (k && k.indexOf(LS_PREFIX) === 0) out.push(k); } } catch (e) {} return out; }
  function xmlEsc(s) { return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;'); }
  function slug(s) { return String(s).toLowerCase().replace(/&/g, 'and').replace(/[^a-z0-9.]+/g, '-').replace(/\./g, '-').replace(/^-+|-+$/g, '') || 'route'; }

  /* ---------- engine host: Worker from this file's engine source, or the main thread ---------- */
  var engineMain = RtRunArtEngine();
  function makeHost() {
    var w = null, seq = 0, waiting = {};
    try {
      var src = '(' + RtRunArtEngine.toString() + ')(self);';
      var url = URL.createObjectURL(new Blob([src], { type: 'text/javascript' }));
      w = new Worker(url);
      w.onmessage = function (ev) {
        var m = ev.data, cb = waiting[m.id];
        if (!cb) return;
        if (m.step) { if (cb.step) cb.step(m.step, m.n); return; }
        delete waiting[m.id];
        if (m.ok) cb.res(m.res); else { var e = new Error(m.res.code); e.code = m.res.code; e.info = m.res.info; cb.rej(e); }
      };
      w.onerror = function () { w = null; host.mode = 'main'; Object.keys(waiting).forEach(function (k) { var e = new Error('worker'); e.code = 'worker'; waiting[k].rej(e); delete waiting[k]; }); };
    } catch (e) { w = null; }
    function call(cmd, data, step) {
      if (!w) return Promise.reject(Object.assign(new Error('worker'), { code: 'worker' }));
      return new Promise(function (res, rej) { var id = ++seq; waiting[id] = { res: res, rej: rej, step: step }; data.id = id; data.cmd = cmd; w.postMessage(data); });
    }
    var host = {
      mode: w ? 'worker' : 'main',
      ready: w ? withTimeout(call('ping', {}).then(function () { return true; }, function () { return false; }), 3000).then(function (ok) { if (!ok) { host.mode = 'main'; if (w) { try { w.terminate(); } catch (e) {} } w = null; } }) : Promise.resolve(),
      load: function (key, payload) {
        return host.ready.then(function () {
          if (host.mode === 'worker') return call('load', { key: key, payload: payload });
          return new Promise(function (r) { setTimeout(r, 30); }).then(function () { return engineMain.load(key, payload); });
        });
      },
      fit: function (key, opts, step) {
        return host.ready.then(function () {
          if (host.mode === 'worker') return call('fit', { key: key, opts: opts }, step);
          return new Promise(function (r) { setTimeout(r, 30); }).then(function () { return engineMain.fit(key, opts, step); });
        });
      },
      cmd: function (name, key, opts) {
        return host.ready.then(function () {
          if (host.mode === 'worker') return call(name, { key: key, opts: opts });
          return engineMain[name](key, opts);
        });
      }
    };
    return host;
  }

  /* the Worker is made on first use: same host API, created only when ready, load or fit is first asked for */
  function lazyHost() {
    var real = null;
    function get() { if (!real) real = makeHost(); return real; }
    return {
      get mode() { return real ? real.mode : 'pending'; },
      get ready() { return get().ready; },
      load: function (key, payload) { return get().load(key, payload); },
      fit: function (key, opts, step) { return get().fit(key, opts, step); },
      cmd: function (name, key, opts) { return get().cmd(name, key, opts); }
    };
  }

  /* ---------- Overpass query ---------- */
  function overpassQuery(b) {
    var bb = '(' + b.map(function (v) { return v.toFixed(5); }).join(',') + ')';
    var hw = '"highway"~"^(residential|unclassified|tertiary|tertiary_link|secondary|secondary_link|living_street|pedestrian|footway|path|cycleway|service|track|steps)$"';
    var common = '["access"!~"^(private|no)$"]["foot"!~"^(no|private)$"]';
    return '[out:json][timeout:25];(' +
      'way[' + hw + ']' + common + '["service"!~"^(parking_aisle|drive-through)$"]' + bb + ';' +
      'way["highway"~"^(primary|primary_link)$"][~"^sidewalk(:both|:left|:right)?$"~"^(both|left|right|yes|separate)$"]' + common + bb + ';' +
      'way["highway"~"^(residential|unclassified|tertiary|secondary|living_street|pedestrian|footway|path|cycleway|service|track|steps|primary|primary_link)$"]["foot"~"^(yes|designated|permissive)$"]' + bb + ';' +
      ');(._;>;);out skel qt;';
  }

  /* ---------- card helpers (same as the other tools) ---------- */
  function wrap(ctx, text, maxW) {
    var words = text.split(/ +/), lines = [], cur = '';
    for (var i = 0; i < words.length; i++) { var test = cur ? cur + ' ' + words[i] : words[i]; if (cur && ctx.measureText(test).width > maxW) { lines.push(cur); cur = words[i]; } else cur = test; }
    if (cur) lines.push(cur);
    return lines;
  }
  function fitText(ctx, text, font, w, h, maxSize, minSize, lh, maxLines) {
    for (var size = maxSize; size >= minSize; size -= 2) {
      ctx.font = fontStr(font, size);
      var lines = wrap(ctx, text, w), widest = Math.max.apply(null, lines.map(function (l) { return ctx.measureText(l).width; }));
      if (lines.length <= maxLines && widest <= w && lines.length * size * lh <= h) return { size: size, lines: lines };
    }
    ctx.font = fontStr(font, minSize);
    return { size: minSize, lines: wrap(ctx, text, w).slice(0, maxLines) };
  }
  function drawLines(ctx, fit, font, cx, top, h, lh) {
    ctx.font = fontStr(font, fit.size);
    var y = top + (h - fit.lines.length * fit.size * lh) / 2 + fit.size * lh / 2;
    fit.lines.forEach(function (l) { ctx.fillText(l, cx, y); y += fit.size * lh; });
  }
  function canvasToBlob(c) { return new Promise(function (resolve, reject) { try { c.toBlob(function (b) { b ? resolve(b) : reject(new Error('empty')); }, 'image/png'); } catch (e) { reject(e); } }); }

  /* bounds of a fit in meters */
  function fitBounds(f) {
    var b = [Infinity, Infinity, -Infinity, -Infinity];
    function add(xy) { for (var i = 0; i < xy.length; i += 2) { b[0] = Math.min(b[0], xy[i]); b[1] = Math.min(b[1], xy[i + 1]); b[2] = Math.max(b[2], xy[i]); b[3] = Math.max(b[3], xy[i + 1]); } }
    f.parts.forEach(function (p) { add(p.xy); });
    return b;
  }
  function strokeStreets(ctx, streets, tf, clip) {
    if (!streets) return;
    var xy = streets.xy, offs = streets.offs;
    ctx.beginPath();
    for (var w = 0; w < offs.length - 1; w++) {
      var a = offs[w], b = offs[w + 1];
      if (clip) {
        var inside = false;
        for (var i = a; i < b; i++) { var x = xy[2 * i], y = xy[2 * i + 1]; if (x > clip[0] && x < clip[2] && y > clip[1] && y < clip[3]) { inside = true; break; } }
        if (!inside) continue;
      }
      for (i = a; i < b; i++) { var p = tf(xy[2 * i], xy[2 * i + 1]); if (i === a) ctx.moveTo(p[0], p[1]); else ctx.lineTo(p[0], p[1]); }
    }
    ctx.stroke();
  }
  function strokeXY(ctx, xy, tf, upto) {
    ctx.beginPath();
    var n = upto == null ? xy.length / 2 : Math.min(xy.length / 2, upto);
    for (var i = 0; i < n; i++) { var p = tf(xy[2 * i], xy[2 * i + 1]); if (i) ctx.lineTo(p[0], p[1]); else ctx.moveTo(p[0], p[1]); }
    ctx.stroke();
  }

  /* d = { kicker, name, miles, streets, fit, osm } */
  /* the route art inside a box: faint streets, route (connectors lighter and dashed), start pin */
  function routeBox(ctx, d, box, lw) {
    if (!d.fit) return;
    var b = fitBounds(d.fit), bw = b[2] - b[0], bh = b[3] - b[1];
    var sc = Math.min(box[2] / Math.max(bw, 1), box[3] / Math.max(bh, 1)) * .9;
    var mx = (b[0] + b[2]) / 2, my = (b[1] + b[3]) / 2;
    var tf = function (x, y) { return [box[0] + box[2] / 2 + (x - mx) * sc, box[1] + box[3] / 2 - (y - my) * sc]; };
    ctx.save();
    ctx.beginPath(); ctx.rect(box[0], box[1], box[2], box[3]); ctx.clip();
    ctx.lineCap = 'round'; ctx.lineJoin = 'round';
    ctx.strokeStyle = 'rgba(255,249,236,0.16)'; ctx.lineWidth = 2;
    var half = [box[2] / 2 / sc, box[3] / 2 / sc];
    if (d.streets) strokeStreets(ctx, d.streets, tf, [mx - half[0] - 50, my - half[1] - 50, mx + half[0] + 50, my + half[1] + 50]);
    d.fit.parts.forEach(function (p) {
      ctx.strokeStyle = p.c ? 'rgba(255,249,236,0.45)' : COLORS.ink;
      ctx.lineWidth = p.c ? lw * .43 : lw;
      ctx.setLineDash(p.c ? [4, 14] : []);
      strokeXY(ctx, p.xy, tf);
    });
    ctx.setLineDash([]);
    var st = d.fit.parts[0].xy, sp = tf(st[0], st[1]);
    ctx.fillStyle = COLORS.sun; ctx.strokeStyle = COLORS.bg; ctx.lineWidth = lw * .43;
    ctx.beginPath(); ctx.arc(sp[0], sp[1], lw * 1.3, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
    ctx.restore();
  }

  /* Instagram Story image, 1080 x 1920. Everything sits between y 250 and 1670, clear of the story UI */
  function drawStory(ctx, d) {
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.fillStyle = COLORS.bg; ctx.fillRect(0, 0, STORY_W, STORY_H);
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.fillStyle = COLORS.sun;
    drawLines(ctx, fitText(ctx, hand(d.kicker), FONTS.hand, 900, 90, 72, 36, 1.1, 1), FONTS.hand, STORY_W / 2, STORY_SAFE + 20, 90, 1.1);
    routeBox(ctx, d, [60, 380, 960, 900], 16);
    ctx.fillStyle = COLORS.ink;
    drawLines(ctx, fitText(ctx, d.name || '', FONTS.big, 940, 150, 150, 70, .95, 1), FONTS.big, STORY_W / 2, 1295, 150, .95);
    ctx.fillStyle = COLORS.sun;
    drawLines(ctx, fitText(ctx, hand(d.stats || ''), FONTS.hand, 940, 76, 64, 34, 1.1, 1), FONTS.hand, STORY_W / 2, 1450, 76, 1.1);
    ctx.fillStyle = 'rgba(255,249,236,0.92)';
    ctx.font = '700 34px Archivo, "Helvetica Neue", Arial, sans-serif';
    ctx.fillText(d.made || '', STORY_W / 2, 1558, 940);
    ctx.fillStyle = COLORS.ink;
    ctx.font = fontStr(FONTS.hand, 56);
    ctx.fillText(CREDIT, STORY_W / 2, 1612);
    ctx.font = '600 22px Arial, Helvetica, sans-serif';
    ctx.fillStyle = 'rgba(255,249,236,0.75)';
    ctx.fillText(d.osm || 'Map data © OpenStreetMap contributors', STORY_W / 2, 1655);
    ctx.restore();
  }

  function drawCard(ctx, W, H, d) {
    var s = W / CARD_W;
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.scale(s, s);
    ctx.fillStyle = COLORS.bg; ctx.fillRect(0, 0, CARD_W, CARD_H);
    ctx.strokeStyle = COLORS.ink; ctx.lineWidth = 4; ctx.strokeRect(40, 40, CARD_W - 80, CARD_H - 80);
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.fillStyle = COLORS.sun;
    drawLines(ctx, fitText(ctx, hand(d.kicker), FONTS.hand, 860, 90, 70, 36, 1.1, 1), FONTS.hand, CARD_W / 2, 80, 90, 1.1);
    routeBox(ctx, d, [90, 190, 900, 760], 14);
    ctx.fillStyle = COLORS.ink;
    drawLines(ctx, fitText(ctx, d.name || '', FONTS.big, 900, 150, 150, 70, .95, 1), FONTS.big, CARD_W / 2, 965, 150, .95);
    ctx.fillStyle = COLORS.sun;
    drawLines(ctx, fitText(ctx, hand(d.miles || ''), FONTS.hand, 860, 80, 64, 36, 1.1, 1), FONTS.hand, CARD_W / 2, 1100, 80, 1.1);
    ctx.fillStyle = COLORS.ink;
    ctx.font = fontStr(FONTS.hand, 54);
    ctx.fillText(CREDIT, CARD_W / 2, 1220);
    ctx.font = '600 22px Arial, Helvetica, sans-serif';
    ctx.fillStyle = 'rgba(255,249,236,0.75)';
    ctx.fillText(d.osm || 'Map data © OpenStreetMap contributors', CARD_W / 2, 1283);
    ctx.restore();
  }

  /* GPX 1.1 track */
  function buildGpx(fit, name) {
    var pts = [];
    fit.parts.forEach(function (p, i) {
      for (var k = 0; k < p.ll.length; k += 2) {
        if (i > 0 && k === 0) continue; /* parts share their joining node */
        pts.push('<trkpt lat="' + p.ll[k].toFixed(6) + '" lon="' + p.ll[k + 1].toFixed(6) + '"></trkpt>');
      }
    });
    return '<?xml version="1.0" encoding="UTF-8"?>\n' +
      '<gpx version="1.1" creator="Run &amp; Tell Run Art Route Maker (runntell.com)" xmlns="http://www.topografix.com/GPX/1/1" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xsi:schemaLocation="http://www.topografix.com/GPX/1/1 http://www.topografix.com/GPX/1/1/gpx.xsd">\n' +
      '<metadata><name>' + xmlEsc(name) + '</name><desc>Made with runntell.com. Map data © OpenStreetMap contributors (ODbL).</desc>' +
      '<copyright author="OpenStreetMap contributors"><license>https://opendatacommons.org/licenses/odbl/</license></copyright>' +
      '<link href="https://runntell.com/"><text>Run &amp; Tell</text></link><time>' + new Date().toISOString().replace(/\.\d+Z$/, 'Z') + '</time></metadata>\n' +
      '<trk><name>' + xmlEsc(name) + '</name><type>running</type><trkseg>\n' + pts.join('\n') + '\n</trkseg></trk>\n</gpx>\n';
  }

  /* TCX course (Garmin TrainingCenterDatabase v2): track points with distance, and times at the chosen pace */
  function buildTcx(fit, name, paceSecPerMile, shortName) {
    var pts = [], dist = 0, prev = null, t0 = Date.now(), mps = 1609.344 / (paceSecPerMile || 600);
    fit.parts.forEach(function (p, i) {
      for (var k = 0; k < p.ll.length; k += 2) {
        if (i > 0 && k === 0) continue;
        var la = p.ll[k], lo = p.ll[k + 1];
        if (prev) { var dy = (la - prev[0]) * 110574, dx = (lo - prev[1]) * 111320 * Math.cos(la * Math.PI / 180); dist += Math.sqrt(dx * dx + dy * dy); }
        prev = [la, lo];
        pts.push('<Trackpoint><Time>' + new Date(t0 + dist / mps * 1000).toISOString().replace(/\.\d+Z$/, 'Z') + '</Time><Position><LatitudeDegrees>' + la.toFixed(6) + '</LatitudeDegrees><LongitudeDegrees>' + lo.toFixed(6) + '</LongitudeDegrees></Position><DistanceMeters>' + dist.toFixed(1) + '</DistanceMeters></Trackpoint>');
      }
    });
    var first = fit.parts[0].ll, lastP = fit.parts[fit.parts.length - 1].ll, short = String(shortName || name).replace(/[^A-Za-z0-9 .&-]/g, '').slice(0, 15).trim() || 'Run art';
    return '<?xml version="1.0" encoding="UTF-8"?>\n' +
      '<TrainingCenterDatabase xmlns="http://www.garmin.com/xmlschemas/TrainingCenterDatabase/v2" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xsi:schemaLocation="http://www.garmin.com/xmlschemas/TrainingCenterDatabase/v2 http://www.garmin.com/xmlschemas/TrainingCenterDatabasev2.xsd">\n' +
      '<Courses><Course><Name>' + xmlEsc(short) + '</Name>\n' +
      '<Lap><TotalTimeSeconds>' + (dist / mps).toFixed(0) + '</TotalTimeSeconds><DistanceMeters>' + dist.toFixed(1) + '</DistanceMeters>' +
      '<BeginPosition><LatitudeDegrees>' + first[0].toFixed(6) + '</LatitudeDegrees><LongitudeDegrees>' + first[1].toFixed(6) + '</LongitudeDegrees></BeginPosition>' +
      '<EndPosition><LatitudeDegrees>' + lastP[lastP.length - 2].toFixed(6) + '</LatitudeDegrees><LongitudeDegrees>' + lastP[lastP.length - 1].toFixed(6) + '</LongitudeDegrees></EndPosition><Intensity>Active</Intensity></Lap>\n' +
      '<Track>\n' + pts.join('\n') + '\n</Track>\n' +
      '<Notes>' + xmlEsc(name) + '. Made with runntell.com. Map data (c) OpenStreetMap contributors (ODbL).</Notes></Course></Courses>\n' +
      '<Author xsi:type="Application_t"><Name>Run and Tell Run Art Route Maker</Name><Build><Version><VersionMajor>2</VersionMajor><VersionMinor>0</VersionMinor></Version></Build><LangID>en</LangID><PartNumber>000-00000-00</PartNumber></Author>\n' +
      '</TrainingCenterDatabase>\n';
  }

  /* shape thumbnail (SVG) for the gallery */
  function thumbSvg(T) {
    var pad = .12, W = Math.max(T.w, T.h) + 2 * pad, d = '';
    T.strokes.forEach(function (s) { for (var i = 0; i < s.length; i += 2) d += (i ? 'L' : 'M') + (s[i] + W / 2).toFixed(3) + ' ' + (W / 2 - s[i + 1]).toFixed(3); });
    return '<svg viewBox="0 0 ' + W.toFixed(3) + ' ' + W.toFixed(3) + '" aria-hidden="true" focusable="false"><path d="' + d + '" fill="none" stroke="currentColor" stroke-width="' + (W * .07).toFixed(3) + '" stroke-linejoin="round" stroke-linecap="round"/></svg>';
  }

  /* =================================================================================================== */
  function init(root) {
    if (!root || root.__rtRa) return;
    root.__rtRa = true;
    var data = parseJSON((root.querySelector('[data-rt-ra-data]') || {}).textContent || '') || {};
    var pc = data.page_copy || {}, ui = data.ui || {};
    var demoEl = root.querySelector('[data-rt-ra-demo]');
    var demo = demoEl ? parseJSON(demoEl.textContent) : null;
    var $ = function (s) { return root.querySelector(s); };
    var form = $('[data-rt-ra-form]'), where = $('[data-rt-ra-where]'), btnFind = $('[data-rt-ra-find]'), btnLoc = $('[data-rt-ra-locate]');
    var whereMsg = $('[data-rt-ra-where-msg]'), placeEl = $('[data-rt-ra-place]');
    var gallery = $('[data-rt-ra-gallery]'), custom = $('[data-rt-ra-custom]'), customErr = $('[data-rt-ra-custom-err]');
    var range = $('[data-rt-ra-miles]'), rangeOut = $('[data-rt-ra-miles-out]');
    var paceM = $('[data-rt-ra-pace-min]'), paceS = $('[data-rt-ra-pace-sec]'), paceErr = $('[data-rt-ra-pace-err]');
    var mapWrap = $('[data-rt-ra-map]'), canvas = $('[data-rt-ra-canvas]'), loading = $('[data-rt-ra-loading]'), loadingLine = $('[data-rt-ra-loading-line]');
    var btnHere = $('[data-rt-ra-here]'), zoomIn = $('[data-rt-ra-zoom-in]'), zoomOut = $('[data-rt-ra-zoom-out]');
    var errBox = $('[data-rt-ra-error]'), errText = $('[data-rt-ra-error-text]'), errRetry = $('[data-rt-ra-retry]');
    var result = $('[data-rt-ra-result]'), card = $('[data-rt-ra-card]'), scoreEl = $('[data-rt-ra-score]'), summaryEl = $('[data-rt-ra-summary]'), lowEl = $('[data-rt-ra-low]'), connEl = $('[data-rt-ra-conn]');
    var btnGpx = $('[data-rt-ra-gpx]'), btnCard = $('[data-rt-ra-save]'), btnAnother = $('[data-rt-ra-another]'), btnShape = $('[data-rt-ra-shape]');
    var status = $('[data-rt-ra-status]'), live = $('[data-rt-ra-live]'), cardCanvas = $('[data-rt-ra-card-canvas]'), fitN = $('[data-rt-ra-fit-n]');
    var nearbyEl = $('[data-rt-ra-nearby]'), movedEl = $('[data-rt-ra-moved]'), movedText = $('[data-rt-ra-moved-text]'), moveBack = $('[data-rt-ra-moveback]');
    if (!form || !canvas) return;

    var cfg = {
      /* primary, the section's fallback, then the other public mirrors (deduped) */
      overpass: [root.getAttribute('data-overpass') || DEFAULT_OVERPASS[0], root.getAttribute('data-overpass-fallback') || DEFAULT_OVERPASS[1]].concat(DEFAULT_OVERPASS).filter(function (u, i, a) { return u && a.indexOf(u) === i; }),
      nominatim: root.getAttribute('data-nominatim') || DEFAULT_NOMINATIM,
      photon: root.hasAttribute('data-photon') ? root.getAttribute('data-photon') : DEFAULT_PHOTON
    };
    var host = lazyHost();
    var shapes = (data.shapes || []).filter(function (s) { return engineMain.GALLERY[s.id] || engineMain.GALLERY_TEXT[s.id]; });
    var state = { center: null, dataset: null, streets: null, fits: null, fitIdx: 0, spec: null, run: 0, busy: false, view: null, anim: 0, lastGeo: 0, pending: null, fontsReady: false, moved: null, alt: null, atPin: false, stay: false };
    /* best spot nearby: the engine tries start centers up to NEAR_M from the pin, on one bigger street download */
    var NEAR_M = 2400;
    function nearbyOn() { return !!(nearbyEl && nearbyEl.checked && !state.stay); }
    if (nearbyEl) nearbyEl.addEventListener('change', function () { state.stay = false; mark('nearby', nearbyEl.checked ? '1' : '0'); autoRoute(); });
    var memCache = {}; /* key -> { bbox, text|compact } */

    function mark(k, v) { root.setAttribute('data-ra-' + k, v); } /* status for QA, kept apart from the data-rt-ra-* element hooks */
    function say(msg) { if (status) status.textContent = msg || ''; }
    function announce(msg) { if (live) live.textContent = msg; }
    function showWhere(msg, isErr) { if (!whereMsg) return; whereMsg.textContent = msg || ''; whereMsg.hidden = !msg; whereMsg.classList.toggle('is-err', !!isErr); }
    function showError(msg, retry) {
      if (!errBox) return;
      errText.textContent = msg || '';
      errBox.hidden = !msg;
      if (errRetry) errRetry.hidden = !retry;
      mark('error', msg ? (retry ? 'retry' : 'shown') : '');
    }

    /* ---------- gallery: tabs (Holiday, Running, Fun, Text), each a grid of shape tiles ---------- */
    var tabsEl = $('[data-rt-ra-tabs]'), textPanel = $('[data-rt-ra-panel-text]');
    var rid = root.id || 'rt-ra', byId = {}, tabs = [], lastShape = shapes[0] ? shapes[0].id : null;
    shapes.forEach(function (s) { byId[s.id] = s; });
    /* ?shape=turkey deep link (also christmas-tree, candy-cane, 26.2, 5k) preselects a shape */
    (function () {
      var q = null;
      try { q = new URLSearchParams(window.location.search).get('shape'); } catch (e) {}
      if (!q) return;
      q = String(q).toLowerCase().trim().replace(/[\s-]+/g, '_').replace(/\./g, '');
      var alias = { christmas_tree: 'tree', xmas_tree: 'tree', dino: 'dinosaur', marathon: 't262', half_marathon: 't131', thumbs_up: 'thumbsup', coffee_cup: 'coffee', running_shoe: 'shoe', ice_cream: 'icecream', icecream_cone: 'icecream', easter_egg: 'egg', easter_bunny: 'bunny', rabbit: 'bunny', clover: 'shamrock', birthday_cake: 'cake', birthday: 'cake', american_flag: 'flag', foot: 'footprint', mountains: 'mountain' };
      var id = byId[q] ? q : (alias[q] && byId[alias[q]]) ? alias[q] : byId['t' + q] ? 't' + q : null;
      if (id) { lastShape = id; mark('deeplink', id); }
    })();
    var groups = ((data.gallery && data.gallery.groups) || []).filter(function (g) { return g.id === 'text' ? !!textPanel : (g.shapes || []).some(function (id) { return byId[id]; }); });
    if (!groups.length) groups = [{ id: 'all', label: '', shapes: shapes.map(function (s) { return s.id; }) }];
    var listed = {};
    groups.forEach(function (g) { (g.shapes || []).forEach(function (id) { listed[id] = 1; }); });
    var extra = shapes.filter(function (s) { return !listed[s.id]; }).map(function (s) { return s.id; });
    if (extra.length) { var g0 = groups.filter(function (g) { return g.id !== 'text'; })[0]; if (g0) g0.shapes = (g0.shapes || []).concat(extra); }
    function makeTile(s, name, checked) {
      var T = engineMain.template({ kind: 'shape', id: s.id, label: s.label });
      var id = rid + '-shape-' + s.id;
      var wrapEl = document.createElement('div');
      wrapEl.className = 'rt-ra__tile';
      wrapEl.innerHTML = '<input type="radio" name="' + name + '" class="rt-ra__tile-input" id="' + id + '" value="' + s.id + '"' + (checked ? ' checked' : '') + '>' +
        '<label class="rt-ra__tile-label" for="' + id + '">' + thumbSvg(T) + '<span class="rt-ra__tile-name"></span></label>';
      wrapEl.querySelector('.rt-ra__tile-name').textContent = s.label;
      return wrapEl;
    }
    groups.forEach(function (g) {
      var panel;
      if (g.id === 'text') panel = textPanel;
      else {
        panel = document.createElement('div');
        panel.className = 'rt-ra__gallery';
        (g.shapes || []).forEach(function (id) { if (byId[id]) panel.appendChild(makeTile(byId[id], rid + '-shape-' + g.id, id === lastShape)); });
        gallery.appendChild(panel);
      }
      panel.id = rid + '-panel-' + g.id;
      panel.setAttribute('data-rt-ra-panel', g.id);
      if (!tabsEl || groups.length < 2) return;
      var tab = document.createElement('button');
      tab.type = 'button'; tab.className = 'rt-ra__tab'; tab.id = rid + '-tab-' + g.id;
      tab.setAttribute('role', 'tab'); tab.setAttribute('aria-controls', panel.id); tab.setAttribute('data-rt-ra-tab', g.id);
      tab.textContent = g.label;
      panel.setAttribute('role', 'tabpanel'); panel.setAttribute('aria-labelledby', tab.id);
      tab.addEventListener('click', function () { selectTab(g.id); });
      tab.addEventListener('keydown', function (e) {
        var i = tabs.indexOf(tab), n = tabs.length, j = null;
        if (e.key === 'ArrowRight') j = (i + 1) % n; else if (e.key === 'ArrowLeft') j = (i - 1 + n) % n; else if (e.key === 'Home') j = 0; else if (e.key === 'End') j = n - 1;
        if (j == null) return;
        e.preventDefault();
        selectTab(tabs[j].getAttribute('data-rt-ra-tab'), true);
      });
      tabsEl.appendChild(tab);
      tabs.push(tab);
    });
    if (tabsEl) tabsEl.hidden = tabs.length < 2;
    function selectTab(id, focus) {
      tabs.forEach(function (t) {
        var on = t.getAttribute('data-rt-ra-tab') === id;
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.tabIndex = on ? 0 : -1;
        if (on && focus) t.focus();
      });
      groups.forEach(function (g) { var p = root.querySelector('[data-rt-ra-panel="' + g.id + '"]'); if (p) p.hidden = tabs.length > 1 && g.id !== id; });
      mark('tab', id);
    }
    function tabOf(shapeId) { var g = groups.filter(function (x) { return (x.shapes || []).indexOf(shapeId) >= 0; })[0]; return g ? g.id : groups[0].id; }
    function selectedTabEl() { return tabs.filter(function (t) { return t.getAttribute('aria-selected') === 'true'; })[0]; }
    if (tabs.length) selectTab(tabOf(lastShape));
    function radios() { return Array.prototype.slice.call(gallery.querySelectorAll('input[type=radio]')); }
    function checkShape(id) { radios().forEach(function (r) { r.checked = r.value === id; }); }
    radios().forEach(function (r) {
      r.addEventListener('change', function () {
        if (!r.checked) return;
        radios().forEach(function (o) { if (o !== r) o.checked = false; }); /* one choice across all tabs */
        lastShape = r.value;
        if (custom) { custom.value = ''; showCustomErr(false); }
        paintTextMiles(); drawMap(); autoRoute();
      });
    });
    function showCustomErr(on) { if (customErr) customErr.hidden = !on; if (custom) { if (on) custom.setAttribute('aria-invalid', 'true'); else custom.removeAttribute('aria-invalid'); } }
    /* longer text needs more miles: suggest a distance (letters about 450 m tall read on most grids) */
    var tMiles = $('[data-rt-ra-text-miles]'), tMilesText = $('[data-rt-ra-text-miles-text]'), tMilesBtn = $('[data-rt-ra-text-miles-btn]');
    function textMilesNeed() {
      var sp = currentSpec(); if (!sp || sp.kind !== 'text') return 0;
      var T; try { T = engineMain.template(sp); } catch (e) { return 0; }
      return Math.min(15, Math.ceil(T.idealLen * 1.2 * 450 / engineMain.M_PER_MI * 2) / 2);
    }
    function paintTextMiles() {
      if (!tMiles) return;
      var need = textMilesNeed(), show = need > miles() + .01;
      tMiles.hidden = !show;
      if (!show) return;
      tMilesText.textContent = fill(ui.text_miles_hint || '', { miles: String(need) });
      tMilesBtn.textContent = fill(ui.text_miles_btn || 'Use {miles} miles', { miles: String(need) });
      tMilesBtn.setAttribute('data-miles', String(need));
    }
    if (tMilesBtn) tMilesBtn.addEventListener('click', function () {
      range.value = tMilesBtn.getAttribute('data-miles'); paintMiles(); paintTextMiles(); drawMap(); autoRoute();
    });
    if (custom) custom.addEventListener('input', function () {
      var v = custom.value.trim();
      if (v) radios().forEach(function (r) { r.checked = false; });
      else if (lastShape) checkShape(lastShape);
      showCustomErr(!!v && !engineMain.textOk(v));
      paintTextMiles();
      drawMap();
      clearTimeout(customTimer);
      if (v && engineMain.textOk(v)) customTimer = setTimeout(autoRoute, 700);
    });
    var customTimer = 0;
    /* after a first result, changing the shape or distance re-runs the fit right away */
    function autoRoute() { if (state.fits && !state.busy && currentSpec()) makeRoute(); }
    function currentSpec() {
      var v = custom ? custom.value.trim() : '';
      if (v) {
        if (!engineMain.textOk(v)) return null;
        var t = engineMain.cleanText(v);
        return { kind: 'text', text: t, label: t === 'R&T' ? 'Run & Tell' : t, scoreName: t === 'R&T' ? 'Run & Tell' : t, id: 'custom' };
      }
      var r = radios().filter(function (x) { return x.checked; })[0];
      var s = byId[r ? r.value : lastShape] || shapes[0];
      return { kind: 'shape', id: s.id, label: s.label, scoreName: /^t\d|^t5k$|^pr$/.test(s.id) ? s.label : s.label.toLowerCase() };
    }

    /* ---------- distance and pace ---------- */
    function miles() { var v = parseFloat(range.value); return isFinite(v) ? v : 5; }
    function paintMiles() { if (rangeOut) rangeOut.textContent = fill(pc.distance_format || '{miles} miles', { miles: String(miles()) }); range.setAttribute('aria-valuetext', rangeOut ? rangeOut.textContent : ''); }
    range.addEventListener('input', function () { paintMiles(); paintTextMiles(); if (!state.fits) drawMap(); });
    range.addEventListener('change', function () { autoRoute(); });
    paintMiles();
    function readPace() {
      var m = String(paceM ? paceM.value : '').trim(), s = String(paceS ? paceS.value : '').trim();
      if (!m && !s) return 600;
      var mi = m ? Number(m) : 0, si = s ? Number(s) : 0;
      if (!isFinite(mi) || !isFinite(si) || mi < 0 || si < 0 || si > 59 || mi % 1 || si % 1) return null;
      var t = mi * 60 + si;
      return t >= 240 && t <= 1500 ? t : null;
    }
    [paceM, paceS].forEach(function (inp) { if (inp) inp.addEventListener('input', function () { var ok = readPace() != null; if (paceErr) paceErr.hidden = ok; [paceM, paceS].forEach(function (x) { if (ok) x.removeAttribute('aria-invalid'); else x.setAttribute('aria-invalid', 'true'); }); if (ok && state.fits) paintResult(); }); });

    /* ---------- map canvas ---------- */
    var ctx = canvas.getContext('2d');
    function cssSize() { var r = canvas.getBoundingClientRect(); return [Math.max(1, r.width), Math.max(1, r.height)]; }
    function resize() {
      var sz = cssSize(), dpr = Math.min(window.devicePixelRatio || 1, 2.5);
      var w = Math.round(sz[0] * dpr), h = Math.round(sz[1] * dpr);
      if (canvas.width !== w || canvas.height !== h) { canvas.width = w; canvas.height = h; }
      return { w: sz[0], h: sz[1], dpr: dpr };
    }
    function viewFit(b, pad) {
      var sz = cssSize(), bw = Math.max(b[2] - b[0], 50), bh = Math.max(b[3] - b[1], 50);
      state.view = { x: (b[0] + b[2]) / 2, y: (b[1] + b[3]) / 2, s: Math.min(sz[0] / bw, sz[1] / bh) * (pad || .86) };
    }
    function tfOf(view, w, h) { return function (x, y) { return [w / 2 + (x - view.x) * view.s, h / 2 - (y - view.y) * view.s]; }; }
    function ghostFit() {
      /* the shape outline at the planned size, before routing */
      if (!state.center || !state.dataset) return null;
      var spec = currentSpec(); if (!spec) return null;
      var T, pl;
      try { T = engineMain.template(spec); pl = engineMain.plan(spec, miles() * engineMain.M_PER_MI); } catch (e) { return null; }
      return T.strokes.map(function (s) { var o = []; for (var i = 0; i < s.length; i += 2) o.push(state.center.x + s[i] * pl.h0, state.center.y + s[i + 1] * pl.h0); return o; });
    }
    function drawMap(progress) {
      var R = resize(), w = R.w, h = R.h;
      ctx.setTransform(R.dpr, 0, 0, R.dpr, 0, 0);
      ctx.fillStyle = COLORS.mapBg; ctx.fillRect(0, 0, w, h);
      if (!state.streets || !state.view) {
        ctx.fillStyle = '#3F3D8F'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle'; ctx.font = '600 16px Archivo, Helvetica, Arial, sans-serif';
        ctx.fillText(ui.map_empty || 'Pick a spot to see your streets here.', w / 2, h / 2);
        canvas.setAttribute('aria-label', ui.map_empty || 'Map');
        return;
      }
      var tf = tfOf(state.view, w, h);
      ctx.lineCap = 'round'; ctx.lineJoin = 'round';
      ctx.strokeStyle = COLORS.street; ctx.lineWidth = state.view.s > 1 ? 2 : state.view.s > .3 ? 1.2 : .8;
      var half = [w / 2 / state.view.s, h / 2 / state.view.s];
      strokeStreets(ctx, state.streets, tf, [state.view.x - half[0] - 200, state.view.y - half[1] - 200, state.view.x + half[0] + 200, state.view.y + half[1] + 200]);
      var f = state.fits && state.fits[state.fitIdx];
      if (f) {
        /* ideal outline, faint */
        ctx.strokeStyle = 'rgba(255,214,0,0.55)'; ctx.lineWidth = 5; ctx.setLineDash([]);
        f.ideal.forEach(function (s) { strokeXY(ctx, s, tf); });
        var total = 0; f.parts.forEach(function (p) { total += p.xy.length / 2; });
        var left = progress == null ? Infinity : Math.floor(total * progress);
        f.parts.forEach(function (p) {
          if (left <= 0) return;
          ctx.strokeStyle = p.c ? 'rgba(15,11,227,0.45)' : COLORS.route;
          ctx.lineWidth = p.c ? 2.5 : 4;
          ctx.setLineDash(p.c ? [3, 6] : []);
          strokeXY(ctx, p.xy, tf, left);
          left -= p.xy.length / 2;
        });
        ctx.setLineDash([]);
        if (state.moved && !state.atPin && state.center && state.center.x != null) {
          var yp = tf(state.center.x, state.center.y);
          ctx.fillStyle = '#fff'; ctx.strokeStyle = COLORS.route; ctx.lineWidth = 3;
          ctx.beginPath(); ctx.arc(yp[0], yp[1], 6, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
          ctx.fillStyle = COLORS.route; ctx.font = '700 12px Archivo, Helvetica, Arial, sans-serif'; ctx.textAlign = 'left'; ctx.textBaseline = 'middle';
          ctx.lineWidth = 4; ctx.strokeStyle = '#fff'; ctx.strokeText(ui.your_spot || 'Your spot', yp[0] + 10, yp[1]); ctx.fillText(ui.your_spot || 'Your spot', yp[0] + 10, yp[1]);
        }
        var sp = tf(f.parts[0].xy[0], f.parts[0].xy[1]);
        ctx.fillStyle = COLORS.sun; ctx.strokeStyle = COLORS.route; ctx.lineWidth = 3;
        ctx.beginPath(); ctx.arc(sp[0], sp[1], 8, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
        ctx.fillStyle = COLORS.route; ctx.font = '800 13px Archivo, Helvetica, Arial, sans-serif'; ctx.textAlign = 'left'; ctx.textBaseline = 'middle';
        ctx.lineWidth = 4; ctx.strokeStyle = '#fff'; ctx.strokeText(ui.start || 'Start', sp[0] + 12, sp[1]); ctx.fillText(ui.start || 'Start', sp[0] + 12, sp[1]);
        if (state.edit && state.flat) drawHandles(tf);
      } else {
        var g = ghostFit();
        if (g) { ctx.strokeStyle = 'rgba(15,11,227,0.35)'; ctx.lineWidth = 3; ctx.setLineDash([6, 6]); g.forEach(function (s) { strokeXY(ctx, s, tf); }); ctx.setLineDash([]); }
      }
      /* crosshair: the center the next route uses */
      ctx.strokeStyle = 'rgba(15,11,227,0.6)'; ctx.lineWidth = 2;
      ctx.beginPath(); ctx.moveTo(w / 2 - 9, h / 2); ctx.lineTo(w / 2 + 9, h / 2); ctx.moveTo(w / 2, h / 2 - 9); ctx.lineTo(w / 2, h / 2 + 9); ctx.stroke();
    }
    function animateRoute() {
      cancelAnimationFrame(state.anim);
      var reduce = window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reduce) { drawMap(); return; }
      var t0 = performance.now(), dur = 1400;
      (function step(t) { var p = Math.min(1, (t - t0) / dur); drawMap(p); if (p < 1) state.anim = requestAnimationFrame(step); })(t0);
    }
    window.addEventListener('resize', function () { drawMap(); });

    /* pan and zoom: pointer drag, pinch, wheel, keys, buttons */
    var pointers = {}, pinch = null;
    function zoomAt(f, px, py) {
      if (!state.view) return;
      var sz = cssSize(), v = state.view;
      var mx = v.x + (px - sz[0] / 2) / v.s, my = v.y - (py - sz[1] / 2) / v.s;
      v.s = Math.max(.01, Math.min(8, v.s * f));
      v.x = mx - (px - sz[0] / 2) / v.s; v.y = my + (py - sz[1] / 2) / v.s;
      drawMap(); movedView();
    }
    function movedView() { if (btnHere && state.center) btnHere.hidden = false; }
    canvas.addEventListener('pointerdown', function (e) {
      if (!state.view) return;
      canvas.setPointerCapture(e.pointerId);
      if (state.edit && !state.editBusy) {
        var k = hitHandle(e);
        if (k >= 0) { var w0 = toWorld(e); state.drag = { k: k, id: e.pointerId, wx: w0[0], wy: w0[1], px: e.clientX, py: e.clientY, moved: false }; state.sel = k; drawMap(); e.preventDefault(); return; }
      }
      pointers[e.pointerId] = [e.clientX, e.clientY];
    });
    canvas.addEventListener('pointermove', function (e) {
      if (state.drag && state.drag.id === e.pointerId) {
        var w1 = toWorld(e); state.drag.wx = w1[0]; state.drag.wy = w1[1];
        if (Math.hypot(e.clientX - state.drag.px, e.clientY - state.drag.py) > 4) state.drag.moved = true;
        drawMap(); return;
      }
      if (!pointers[e.pointerId] || !state.view) return;
      var ids = Object.keys(pointers);
      if (ids.length === 2) {
        var o = ids.filter(function (i) { return +i !== e.pointerId; })[0];
        var d0 = Math.hypot(pointers[e.pointerId][0] - pointers[o][0], pointers[e.pointerId][1] - pointers[o][1]);
        var d1 = Math.hypot(e.clientX - pointers[o][0], e.clientY - pointers[o][1]);
        var r = canvas.getBoundingClientRect();
        if (d0 > 0) zoomAt(d1 / d0, (e.clientX + pointers[o][0]) / 2 - r.left, (e.clientY + pointers[o][1]) / 2 - r.top);
      } else {
        state.view.x -= (e.clientX - pointers[e.pointerId][0]) / state.view.s;
        state.view.y += (e.clientY - pointers[e.pointerId][1]) / state.view.s;
        drawMap(); movedView();
      }
      pointers[e.pointerId] = [e.clientX, e.clientY];
    });
    function up(e) {
      if (state.drag && state.drag.id === e.pointerId) { var d = state.drag; state.drag = null; if (d.moved && e.type === 'pointerup') applyDrag(d.k, d.wx, d.wy); else drawMap(); return; }
      delete pointers[e.pointerId];
    }
    canvas.addEventListener('pointerup', up); canvas.addEventListener('pointercancel', up);
    canvas.addEventListener('wheel', function (e) { if (!state.view) return; e.preventDefault(); var r = canvas.getBoundingClientRect(); zoomAt(e.deltaY < 0 ? 1.15 : 1 / 1.15, e.clientX - r.left, e.clientY - r.top); }, { passive: false });
    canvas.addEventListener('keydown', function (e) {
      if (!state.view) return;
      if (state.edit && editKey(e)) { e.preventDefault(); return; }
      var step = 60 / state.view.s, k = e.key;
      if (k === 'ArrowLeft') state.view.x -= step; else if (k === 'ArrowRight') state.view.x += step;
      else if (k === 'ArrowUp') state.view.y += step; else if (k === 'ArrowDown') state.view.y -= step;
      else if (k === '+' || k === '=') { var s1 = cssSize(); zoomAt(1.25, s1[0] / 2, s1[1] / 2); e.preventDefault(); return; }
      else if (k === '-') { var s2 = cssSize(); zoomAt(.8, s2[0] / 2, s2[1] / 2); e.preventDefault(); return; }
      else return;
      e.preventDefault(); drawMap(); movedView();
    });
    if (zoomIn) zoomIn.addEventListener('click', function () { var s = cssSize(); zoomAt(1.4, s[0] / 2, s[1] / 2); });
    if (zoomOut) zoomOut.addEventListener('click', function () { var s = cssSize(); zoomAt(1 / 1.4, s[0] / 2, s[1] / 2); });
    if (btnHere) btnHere.addEventListener('click', function () {
      if (!state.view || !state.dataset) return;
      var ll = engineMain.unproject(state.dataset.o, state.view.x, state.view.y);
      setCenter(ll[0], ll[1], ui.place_map || 'the map center', true);
      state.stay = true; /* placed by hand: route it right here, no nearby search */
      btnHere.hidden = true;
      if (state.fits) makeRoute();
    });

    /* ---------- loading lines ---------- */
    var loadTimer = 0;
    function setLoading(on) {
      if (!loading) return;
      loading.hidden = !on;
      mapWrap.setAttribute('aria-busy', on ? 'true' : 'false');
      clearInterval(loadTimer);
      if (!on) return;
      var spec = currentSpec(), lines = (pc.loading_lines || []).map(function (l) { return spec && spec.kind === 'shape' && spec.id !== 'turkey' ? l.replace(/\bturkey\b/, spec.label.toLowerCase()) : spec && spec.kind === 'text' ? l.replace(/\bthe turkey\b/, spec.text) : l; });
      var i = 0;
      loadingLine.textContent = lines[0] || '';
      loadTimer = setInterval(function () { i = (i + 1) % Math.max(1, lines.length); loadingLine.textContent = lines[i] || ''; }, 1600);
    }

    /* ---------- where: location, place suggestions (combobox), search, demo areas ---------- */
    function setCenter(lat, lon, label, keepView) {
      state.center = { lat: lat, lon: lon, label: label };
      if (state.dataset) { var p = engineMain.project(state.dataset.o, lat, lon); state.center.x = p[0]; state.center.y = p[1]; }
      if (placeEl) placeEl.textContent = label ? fill(ui.place_set || 'Starting near {place}.', { place: label }) : '';
      mark('center', lat.toFixed(5) + ',' + lon.toFixed(5));
      if (!keepView) { state.fits = null; state.stay = false; }
    }
    function pickedPlace(lat, lon, label) {
      setCenter(lat, lon, label);
      showWhere('');
      showError('');
      result.hidden = true;
      return prepareData(false).then(function () { if (state.dataset) { viewAroundCenter(); drawMap(); } mark('place-done', '1'); }).catch(function (e) { mark('place-done', '1'); handleError(e); });
    }
    function viewAroundCenter() {
      var pl; try { pl = engineMain.plan(currentSpec() || { kind: 'shape', id: 'turkey' }, miles() * engineMain.M_PER_MI); } catch (e) { pl = { radius: 1500 }; }
      var r = Math.max(pl.radius - 200, 400);
      viewFit([state.center.x - r, state.center.y - r, state.center.x + r, state.center.y + r], .95);
    }
    function inArea(a, lat, lon) { var b = a.bbox; return !!b && lat >= b[0] && lat <= b[2] && lon >= b[1] && lon <= b[3]; }
    function offerAreas(msg) {
      showWhere(msg, false);
      var first = root.querySelector('[data-rt-ra-area]');
      if (first) first.focus();
    }
    if (btnLoc) btnLoc.addEventListener('click', function () {
      closeSugg();
      if (!navigator.geolocation) {
        mark('geo', 'unavailable');
        if (demo) offerAreas(ui.demo_geo_fail || ui.demo_location || '');
        else { showWhere(ui.error_geo || '', true); if (where) where.focus(); }
        return;
      }
      showWhere(ui.locating || 'Finding you...', false);
      btnLoc.disabled = true;
      navigator.geolocation.getCurrentPosition(function (pos) {
        btnLoc.disabled = false;
        var lat = pos.coords.latitude, lon = pos.coords.longitude, you = ui.place_you || 'your location';
        if (demo) {
          var a = demo.areas.filter(function (x) { return inArea(x, lat, lon); })[0];
          if (!a) { mark('geo', 'outside'); offerAreas(ui.demo_geo_outside || ''); return; }
          mark('geo', 'granted');
          if (where) where.value = '';
          return pickArea(a, { lat: lat, lon: lon, label: you });
        }
        mark('geo', 'granted');
        if (where) where.value = '';
        lastQuery = '';
        pickedPlace(lat, lon, you);
      }, function () {
        btnLoc.disabled = false;
        mark('geo', 'denied');
        if (demo) { offerAreas(ui.demo_geo_fail || ''); return; }
        showWhere(ui.error_geo || '', true);
        if (where) where.focus();
      }, { enableHighAccuracy: false, timeout: 12000, maximumAge: 600000 });
    });

    /* place suggestions: Photon (photon.komoot.io, built for search as you type) on the live page,
       the embedded place list in the demo. Nominatim is only used when someone submits text without
       picking a suggestion (its policy does not allow autocomplete). */
    var US_ST = { Alabama: 'AL', Alaska: 'AK', Arizona: 'AZ', Arkansas: 'AR', California: 'CA', Colorado: 'CO', Connecticut: 'CT', Delaware: 'DE', 'District of Columbia': 'DC', Florida: 'FL', Georgia: 'GA', Hawaii: 'HI', Idaho: 'ID', Illinois: 'IL', Indiana: 'IN', Iowa: 'IA', Kansas: 'KS', Kentucky: 'KY', Louisiana: 'LA', Maine: 'ME', Maryland: 'MD', Massachusetts: 'MA', Michigan: 'MI', Minnesota: 'MN', Mississippi: 'MS', Missouri: 'MO', Montana: 'MT', Nebraska: 'NE', Nevada: 'NV', 'New Hampshire': 'NH', 'New Jersey': 'NJ', 'New Mexico': 'NM', 'New York': 'NY', 'North Carolina': 'NC', 'North Dakota': 'ND', Ohio: 'OH', Oklahoma: 'OK', Oregon: 'OR', Pennsylvania: 'PA', 'Puerto Rico': 'PR', 'Rhode Island': 'RI', 'South Carolina': 'SC', 'South Dakota': 'SD', Tennessee: 'TN', Texas: 'TX', Utah: 'UT', Vermont: 'VT', Virginia: 'VA', Washington: 'WA', 'West Virginia': 'WV', Wisconsin: 'WI', Wyoming: 'WY' };
    var suggList = $('[data-rt-ra-sugg]'), suggLive = $('[data-rt-ra-sugg-live]');
    var sugg = { items: [], active: -1, timer: 0, seq: 0, ctl: null, cache: {}, q: '' };
    var SUGG_MIN = 3, SUGG_WAIT = 300;
    function norm(s) { return String(s || '').toLowerCase().replace(/[’']/g, '').replace(/[^a-z0-9]+/g, ' ').trim(); }
    /* township, county and similar admin names are not what people call a place ("North Chicago Township") */
    var ADMIN_RE = /\b(township|county|parish|precinct|census|unorganized|borough of|town of)\b/i;
    function cityOf(list, name) { for (var i = 0; i < list.length; i++) { var c = list[i]; if (c && c !== name && !ADMIN_RE.test(c)) return c; } return ''; }
    function photonLabel(pr) {
      var name = pr.name || (pr.street ? (pr.housenumber ? pr.housenumber + ' ' : '') + pr.street : '') || pr.postcode || pr.city || '';
      var hood = /^(district|locality|suburb|neighbourhood|quarter)$/.test(pr.type || '') || /^(suburb|neighbourhood|quarter|city_block)$/.test(pr.osm_value || '');
      var city = cityOf([pr.city, pr.town, pr.village, hood ? '' : pr.locality, hood ? '' : pr.district, pr.county], name);
      var us = String(pr.countrycode || '').toUpperCase() === 'US';
      var st = us ? (US_ST[pr.state] || pr.state || '') : (pr.state || '');
      /* a neighborhood reads as "Lincoln Park, Chicago": neighborhood and city */
      if (hood && city && us) return name + ', ' + city;
      var parts = [name];
      if (city && city !== name) parts.push(city);
      if (us) { if (st && st !== name) parts.push(st); }
      else { if (!city && st && st !== name) parts.push(st); if (pr.country && pr.country !== name) parts.push(pr.country); }
      return parts.filter(Boolean).join(', ');
    }
    function nominatimLabel(h, q) {
      var a = h.address || {}, name = h.name || String(h.display_name || q).split(',')[0].trim();
      var hood = /^(suburb|neighbourhood|quarter|city_district)$/.test(h.addresstype || h.type || '');
      var city = cityOf([a.city, a.town, a.village, a.hamlet], name);
      if (city) return hood || !a.state ? name + ', ' + city : name + ', ' + (US_ST[a.state] || a.state);
      if (a.state && a.state !== name) return name + ', ' + (US_ST[a.state] || a.state);
      var rest = String(h.display_name || q).split(',').map(function (x) { return x.trim(); }).filter(function (x, i) { return i && !ADMIN_RE.test(x) && !/^\d/.test(x) && !/^(USA|United States( of America)?)$/i.test(x); });
      return [name].concat(rest.slice(0, 1)).join(', ');
    }
    function photonItems(j) {
      var seen = {}, out = [];
      ((j && j.features) || []).forEach(function (f) {
        var c = f && f.geometry && f.geometry.coordinates, pr = (f && f.properties) || {};
        if (!c || c.length < 2) return;
        var label = photonLabel(pr);
        if (!label || seen[label]) return;
        seen[label] = 1;
        out.push({ label: label, lat: +c[1], lon: +c[0], us: String(pr.countrycode || '').toUpperCase() === 'US' });
      });
      /* US first (the store ships to the US), keeping Photon's order otherwise */
      return out.filter(function (x) { return x.us; }).concat(out.filter(function (x) { return !x.us; })).slice(0, 5);
    }
    function demoMatches(q) {
      var toks = norm(q).split(' ').filter(Boolean);
      if (!toks.length) return [];
      return (demo.places || []).map(function (p, i) {
        var words = norm(p.label + ' ' + (p.alias || '')).split(' ');
        var ok = toks.every(function (t) { return words.some(function (w) { return w.indexOf(t) === 0; }); });
        return ok ? { p: p, rank: (norm(p.label).indexOf(norm(q)) === 0 ? 0 : 1) + (p.top ? 0 : 2), i: i } : null;
      }).filter(Boolean).sort(function (a, b) { return a.rank - b.rank || a.i - b.i; }).slice(0, 5).map(function (m) { return m.p; });
    }
    function fetchSugg(q) {
      var key = norm(q);
      if (sugg.cache[key]) return Promise.resolve(sugg.cache[key]);
      if (demo) { var d = demoMatches(q); sugg.cache[key] = d; return Promise.resolve(d); }
      if (!cfg.photon) return Promise.resolve([]);
      if (sugg.ctl) { try { sugg.ctl.abort(); } catch (e) {} }
      sugg.ctl = window.AbortController ? new AbortController() : null;
      var url = cfg.photon + (cfg.photon.indexOf('?') >= 0 ? '&' : '?') + 'q=' + encodeURIComponent(q) + '&limit=5&lang=en&lat=39.8&lon=-98.6';
      mark('sugg-fetch', String((+root.getAttribute('data-ra-sugg-fetch') || 0) + 1));
      return fetch(url, { headers: { 'Accept': 'application/json' }, signal: sugg.ctl ? sugg.ctl.signal : undefined, referrerPolicy: 'strict-origin-when-cross-origin' })
        .then(function (r) { if (!r.ok) throw new Error('photon ' + r.status); return r.json(); })
        .then(function (j) { var items = photonItems(j); sugg.cache[key] = items; return items; });
    }
    function optId(i) { return (root.id || 'rt-ra') + '-opt-' + i; }
    function renderSugg(items) {
      if (!suggList) return;
      suggList.innerHTML = '';
      items.forEach(function (it, i) {
        var li = document.createElement('li');
        li.id = optId(i); li.className = 'rt-ra__opt'; li.setAttribute('role', 'option'); li.setAttribute('aria-selected', 'false');
        var parts = it.label.split(', '), b = document.createElement('span');
        b.className = 'rt-ra__opt-main'; b.textContent = parts[0]; li.appendChild(b);
        if (parts.length > 1) li.appendChild(document.createTextNode(', ' + parts.slice(1).join(', ')));
        li.addEventListener('mousedown', function (e) { e.preventDefault(); }); /* keep focus in the field */
        li.addEventListener('click', function () { chooseSugg(i); });
        suggList.appendChild(li);
      });
    }
    function openSugg() {
      if (!suggList || !sugg.items.length) return;
      suggList.hidden = false;
      where.setAttribute('aria-expanded', 'true');
      mark('sugg', 'open');
    }
    function closeSugg() {
      clearTimeout(sugg.timer);
      sugg.active = -1;
      if (!suggList || !where) return;
      suggList.hidden = true;
      where.setAttribute('aria-expanded', 'false');
      where.removeAttribute('aria-activedescendant');
      if (root.getAttribute('data-ra-sugg') === 'open') mark('sugg', 'closed');
    }
    function setActive(i) {
      sugg.active = i;
      Array.prototype.forEach.call(suggList.children, function (li, k) { li.setAttribute('aria-selected', k === i ? 'true' : 'false'); li.classList.toggle('is-active', k === i); });
      if (i >= 0) { where.setAttribute('aria-activedescendant', optId(i)); var el = suggList.children[i]; if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' }); }
      else where.removeAttribute('aria-activedescendant');
    }
    function suggest(q) {
      var seq = ++sugg.seq;
      fetchSugg(q).then(function (items) {
        if (seq !== sugg.seq || !where || where.value.trim() !== q) return;
        sugg.items = items; sugg.q = q; sugg.active = -1;
        if (!items.length) {
          closeSugg();
          mark('sugg', 'none');
          if (demo) showWhere(ui.demo_suggest_none || ui.demo_search_miss || '', false);
          return;
        }
        if (demo) showWhere('');
        renderSugg(items);
        openSugg();
        if (suggLive) suggLive.textContent = fill(items.length === 1 ? (ui.sugg_count_one || '1 place found.') : (ui.sugg_count || '{n} places found.'), { n: items.length });
      }, function () {
        if (seq !== sugg.seq) return;
        sugg.items = []; closeSugg(); mark('sugg', 'error');
      });
    }
    function chooseSugg(i) {
      var it = sugg.items[i];
      if (!it || !where) return;
      where.value = it.label;
      closeSugg();
      showWhere('');
      where.removeAttribute('aria-invalid');
      lastQuery = it.label;
      mark('picked', it.label);
      if (demo) { var a = demo.areas.filter(function (x) { return x.id === it.area; })[0]; if (a) pickArea(a, it).catch(handleError); return; }
      geoCache[it.label] = { lat: it.lat, lon: it.lon, label: it.label };
      pickedPlace(it.lat, it.lon, it.label);
    }
    if (where) {
      where.addEventListener('input', function () {
        where.removeAttribute('aria-invalid');
        var q = where.value.trim();
        clearTimeout(sugg.timer);
        if (q.length < SUGG_MIN) { sugg.seq++; sugg.items = []; closeSugg(); if (demo) showWhere(''); return; }
        sugg.timer = setTimeout(function () { suggest(q); }, SUGG_WAIT);
      });
      where.addEventListener('keydown', function (e) {
        var open = suggList && !suggList.hidden && sugg.items.length > 0;
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          if (!open) { if (sugg.items.length && sugg.q === where.value.trim()) { openSugg(); setActive(0); } return; }
          setActive(sugg.active + 1 >= sugg.items.length ? 0 : sugg.active + 1);
        } else if (e.key === 'ArrowUp') {
          if (!open) return;
          e.preventDefault();
          setActive(sugg.active <= 0 ? sugg.items.length - 1 : sugg.active - 1);
        } else if (e.key === 'Enter') {
          e.preventDefault();
          if (open && sugg.active >= 0) chooseSugg(sugg.active);
          else { closeSugg(); findPlace(); }
        } else if (e.key === 'Escape') {
          if (open) { e.preventDefault(); e.stopPropagation(); closeSugg(); }
        } else if (e.key === 'Tab') closeSugg();
      });
      where.addEventListener('blur', function () { setTimeout(function () { if (document.activeElement !== where) closeSugg(); }, 200); });
    }

    var geoCache = {};
    function geocode(q) {
      q = q.trim();
      if (!q) return Promise.resolve(null);
      if (demo) {
        var dm = demoMatches(q)[0];
        if (dm) return Promise.resolve({ demoPlace: dm });
        var lq = q.toLowerCase();
        var hit = demo.areas.filter(function (a) { return (a.match || []).some(function (m) { return lq.indexOf(m) >= 0; }); })[0];
        return Promise.resolve(hit ? { demoArea: hit } : { demoMiss: true });
      }
      if (geoCache[q]) return Promise.resolve(geoCache[q]);
      /* a suggestion list for this exact text is already here: take its first place, no extra request */
      var cached = sugg.cache[norm(q)];
      if (cached && cached.length) return Promise.resolve({ lat: cached[0].lat, lon: cached[0].lon, label: cached[0].label });
      var waitMs = Math.max(0, 1100 - (Date.now() - state.lastGeo)); /* Nominatim: at most 1 request per second */
      return new Promise(function (r) { setTimeout(r, waitMs); }).then(function () {
        state.lastGeo = Date.now();
        var zip = /^\d{5}$/.test(q);
        var url = cfg.nominatim + (cfg.nominatim.indexOf('?') >= 0 ? '&' : '?') + 'format=jsonv2&limit=1&addressdetails=1&accept-language=en' + (zip ? '&countrycodes=us' : '') + '&q=' + encodeURIComponent(q);
        return fetch(url, { headers: { 'Accept': 'application/json' }, referrerPolicy: 'strict-origin-when-cross-origin' });
      }).then(function (r) { if (!r.ok) throw Object.assign(new Error('geo'), { code: 'geo_busy' }); return r.json(); })
        .then(function (j) {
          var hit = j && j[0] ? { lat: +j[0].lat, lon: +j[0].lon, label: nominatimLabel(j[0], q) } : null;
          if (hit) geoCache[q] = hit;
          return hit;
        });
    }
    var lastQuery = '';
    function findPlace() {
      var q = where ? where.value.trim() : '';
      if (!q) { showWhere(ui.error_where_empty || '', true); return Promise.resolve(false); }
      closeSugg();
      if (btnFind) btnFind.disabled = true;
      showWhere(ui.searching || 'Looking it up...', false);
      return geocode(q).then(function (hit) {
        if (btnFind) btnFind.disabled = false;
        if (hit && hit.demoPlace) {
          var pa = demo.areas.filter(function (x) { return x.id === hit.demoPlace.area; })[0];
          where.value = hit.demoPlace.label; lastQuery = hit.demoPlace.label;
          return pickArea(pa, hit.demoPlace).then(function () { return true; });
        }
        if (hit && hit.demoArea) { lastQuery = q; return pickArea(hit.demoArea).then(function () { return true; }); }
        if (!hit || hit.demoMiss) { showWhere(hit && hit.demoMiss ? ui.demo_search_miss || pc.error_nowhere : pc.error_nowhere, true); mark('geo', 'nowhere'); if (where) where.setAttribute('aria-invalid', 'true'); return false; }
        if (where) where.removeAttribute('aria-invalid');
        lastQuery = q;
        return pickedPlace(hit.lat, hit.lon, hit.label).then(function () { return true; });
      }, function () {
        if (btnFind) btnFind.disabled = false;
        showWhere(ui.error_geo_busy || pc.error_busy, true);
        return false;
      });
    }
    if (btnFind) btnFind.addEventListener('click', function () { findPlace(); });

    /* demo areas */
    function pickArea(a, place) {
      if (!a) return Promise.resolve();
      var c = place || { lat: a.center[0], lon: a.center[1], label: a.label };
      if (where && !place) { where.value = a.label; lastQuery = a.label; }
      var go;
      if (state.dataset && state.dataset.key === 'demo:' + a.id) go = Promise.resolve();
      else { state.dataset = null; go = loadCompact('demo:' + a.id, a.data, null); }
      return go.then(function () {
        setCenter(c.lat, c.lon, c.label);
        showWhere(''); showError(''); result.hidden = true;
        viewAroundCenter(); drawMap();
        mark('place-done', '1');
        Array.prototype.forEach.call(root.querySelectorAll('[data-rt-ra-area]'), function (b) { b.setAttribute('aria-pressed', b.getAttribute('data-rt-ra-area') === a.id ? 'true' : 'false'); });
      });
    }
    if (demo) {
      var areasEl = $('[data-rt-ra-areas]');
      if (areasEl) {
        areasEl.hidden = false;
        demo.areas.forEach(function (a) {
          var b = document.createElement('button');
          b.type = 'button'; b.className = 'rt-ra__btn rt-ra__btn--small'; b.textContent = a.label; b.setAttribute('data-rt-ra-area', a.id); b.setAttribute('aria-pressed', 'false');
          b.addEventListener('click', function () { closeSugg(); pickArea(a); });
          areasEl.appendChild(b);
        });
      }
      if (demo.areas[0]) pickArea(demo.areas[0]).catch(handleError);
    }

    /* ---------- street data: memory cache, localStorage cache, Overpass with fallback ---------- */
    function needBox() {
      var target = miles() * engineMain.M_PER_MI, pl = engineMain.plan(currentSpec() || { kind: 'shape', id: 'turkey' }, target);
      /* fetch enough for any gallery shape at this distance, so "Change shape" reuses the same streets */
      /* only the streets this shape needs (plus a margin): a smaller box answers much faster on busy public servers */
      /* with "best spot nearby" one bigger box is fetched once (the shape's box plus the search ring); any loaded box
         that holds the shape's own box is reused, and the engine only tries the spots the loaded streets cover */
      var r0 = Math.min(Math.max(pl.radius * 1.2, 1200), 5200), r = nearbyOn() ? Math.min(Math.max(r0, pl.half / 1.15 * 1.05 + 60 + NEAR_M), 5200) : r0;
      var c = state.center;
      function box(r) {
        var dLat = r / 110574, dLon = r / (111320 * Math.cos(c.lat * Math.PI / 180));
        /* snap outward to a 0.005 degree grid so nearby requests share a cache entry */
        var q = .005;
        return [Math.floor((c.lat - dLat) / q) * q, Math.floor((c.lon - dLon) / q) * q, Math.ceil((c.lat + dLat) / q) * q, Math.ceil((c.lon + dLon) / q) * q].map(function (v) { return Math.round(v * 1e5) / 1e5; });
      }
      return { plan: pl, bbox: box(r), min: box(r0) };
    }
    function contains(b, n) { return b[0] <= n[0] + 1e-9 && b[1] <= n[1] + 1e-9 && b[2] >= n[2] - 1e-9 && b[3] >= n[3] - 1e-9; }
    function loadCompact(key, compact, bbox) {
      return host.load(key, { compact: compact }).then(function (res) { return adopt(key, res, bbox); });
    }
    function adopt(key, res, bbox) {
      state.dataset = { key: key, o: { lat: res.origin[0], lon: res.origin[1] }, bounds: res.bounds, bbox: bbox, nodes: res.nodes };
      state.streets = res.streets;
      if (state.center) { var p = engineMain.project(state.dataset.o, state.center.lat, state.center.lon); state.center.x = p[0]; state.center.y = p[1]; }
      mark('dataset', key);
      mark('nodes', String(res.nodes));
      return res;
    }
    function fetchOverpass(bbox) {
      var body = 'data=' + encodeURIComponent(overpassQuery(bbox));
      var tried = [], n = cfg.overpass.length, ROUNDS = 2, HEDGE_MS = 3000, LIMIT_MS = 25000;
      /* hedged: start the first mirror, add the next one every few seconds while nothing has answered,
         first good answer wins and the rest are aborted. If every mirror fails, one more round after a short pause. */
      function one(url, ctls) {
        tried.push(url);
        var ctl = window.AbortController ? new AbortController() : null;
        if (ctl) ctls.push(ctl);
        var timer = setTimeout(function () { if (ctl) ctl.abort(); }, LIMIT_MS);
        return fetch(url, { method: 'POST', body: body, headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' }, signal: ctl ? ctl.signal : undefined, referrerPolicy: 'strict-origin-when-cross-origin' })
          .then(function (r) {
            if (!r.ok) throw Object.assign(new Error('http ' + r.status), { code: 'busy', status: r.status });
            return r.text();
          }).then(function (t) {
            clearTimeout(timer);
            if (!/^\s*\{/.test(t)) throw Object.assign(new Error('not json'), { code: 'busy' });
            var head = t.slice(0, 600);
            if (/"remark"\s*:\s*"[^"]*(runtime error|timed out|out of memory|rate_limited)/i.test(head) && !/"type"\s*:\s*"way"/.test(t.slice(0, 200000))) throw Object.assign(new Error('remark'), { code: 'busy' });
            return { url: url, text: t };
          }, function (e) { clearTimeout(timer); throw e; });
      }
      function round(r) {
        return new Promise(function (resolve, reject) {
          var ctls = [], failed = 0, started = 0, done = false, hedge = 0;
          function finish() { done = true; clearTimeout(hedge); ctls.forEach(function (c) { try { c.abort(); } catch (e) {} }); }
          function next() {
            if (done || started >= n) return;
            var url = cfg.overpass[started++];
            if (started > 1 || r > 0) mark('overpass-retry', String(r * n + started - 1));
            one(url, ctls).then(function (res) {
              if (done) return;
              finish(); mark('overpass-used', res.url); resolve(res.text);
            }, function () {
              if (done) return;
              failed++;
              if (failed >= n) { finish(); reject(Object.assign(new Error('busy'), { code: 'busy', tried: tried })); }
              else if (started < n) { clearTimeout(hedge); next(); arm(); } /* a mirror that fails fast hands over to the next one at once */
            });
          }
          function arm() { clearTimeout(hedge); if (started < n) hedge = setTimeout(function () { next(); arm(); }, HEDGE_MS); }
          next(); arm();
        });
      }
      function go(r) {
        return round(r).catch(function (e) {
          if (r + 1 >= ROUNDS) throw e;
          return new Promise(function (res) { setTimeout(res, 2500); }).then(function () { return go(r + 1); });
        });
      }
      return go(0);
    }
    var inflight = {};
    function prepareData(forRoute) {
      if (!state.center) return Promise.resolve();
      if (demo) {
        if (!state.dataset) return Promise.reject(Object.assign(new Error('nowhere'), { code: 'no_center' }));
        return Promise.resolve();
      }
      var nb = needBox();
      if (nb.plan.tooBig) return Promise.reject(Object.assign(new Error('too_big'), { code: 'too_big', info: { maxMi: Math.floor(nb.plan.maxTargetM / engineMain.M_PER_MI * 2) / 2 } }));
      if (state.dataset && state.dataset.bbox && contains(state.dataset.bbox, nb.min)) return Promise.resolve();
      /* memory, then localStorage */
      var keys = Object.keys(memCache).filter(function (k) { return contains(memCache[k].bbox, nb.min); });
      if (keys.length) { var m = memCache[keys[0]]; return host.load(keys[0], m.compact ? { compact: m.compact } : { text: m.text, bbox: m.bbox }).then(function (res) { return adopt(keys[0], res, m.bbox); }); }
      var lk = lsKeys().filter(function (k) { var b = k.slice(LS_PREFIX.length).split(',').map(Number); return b.length === 4 && contains(b, nb.min); })[0];
      if (lk) {
        var stored = parseJSON(lsGet(lk));
        if (stored && stored.v === 1) {
          mark('cache', 'localStorage');
          var bb = lk.slice(LS_PREFIX.length).split(',').map(Number);
          lsSet(lk, lsGet(lk)); /* touch */
          return host.load(lk, { compact: stored }).then(function (res) { memCache[lk] = { bbox: bb, compact: stored }; return adopt(lk, res, bb); });
        }
        lsDel(lk);
      }
      mark('cache', 'network');
      var key = LS_PREFIX + nb.bbox.join(',');
      /* the place-pick prefetch and Make my route can ask for the same box: share one request */
      if (!inflight[key]) inflight[key] = fetchOverpass(nb.bbox).then(function (t) { delete inflight[key]; return t; }, function (e) { delete inflight[key]; throw e; });
      return inflight[key].then(function (text) {
        return host.load(key, { text: text, bbox: nb.bbox });
      }).then(function (res) {
        if (res.compact) {
          memCache = {}; memCache[key] = { bbox: nb.bbox, compact: res.compact };
          storeLS(key, res.compact);
        }
        return adopt(key, res, nb.bbox);
      });
    }
    function storeLS(key, compact) {
      var s = JSON.stringify(compact);
      var keys = lsKeys();
      while (keys.length >= LS_KEEP) { lsDel(keys.shift()); }
      if (!lsSet(key, s)) { lsKeys().forEach(lsDel); lsSet(key, s); }
    }

    /* ---------- route ---------- */
    function errorMessage(e) {
      var c = e && e.code, info = (e && e.info) || {};
      if (c === 'busy') return [pc.error_busy, true];
      if (c === 'no_streets') return [ui.error_no_streets, false];
      if (c === 'too_small') return [fill(ui.error_too_small, { miles: info.needMi }), false];
      if (c === 'too_big') return [fill(ui.error_too_big, { miles: info.maxMi }), false];
      if (c === 'outside') return [info.maxMi >= 2 ? fill(demo ? ui.error_outside_demo : ui.error_outside, { miles: info.maxMi }) : (demo ? ui.error_outside_demo_center : ui.error_off_map), false];
      if (c === 'off_map') return [ui.error_off_map, false];
      if (c === 'no_center') return [ui.error_no_center, false];
      return [ui.error_generic || pc.error_busy, true];
    }
    function handleError(e) {
      setLoading(false);
      state.busy = false;
      var m = errorMessage(e);
      showError(m[0], m[1]);
      mark('state', 'error');
      mark('error-code', (e && e.code) || 'error');
      if (window.console && (!e || !e.code)) console.warn('[rt-run-art]', e);
    }
    function makeRoute(opts) {
      clearTimeout(customTimer);
      closeSugg();
      var spec = currentSpec();
      if (!spec) { showCustomErr(true); if (custom) custom.focus(); return; }
      if (readPace() == null) { if (paceErr) paceErr.hidden = false; if (paceM) paceM.focus(); return; }
      var pre = Promise.resolve(true);
      if (!state.center) {
        if (where && where.value.trim() && !demo) pre = findPlace();
        else if (demo && where && where.value.trim()) pre = findPlace();
        else { showError(ui.error_no_center, false); mark('error-code', 'no_center'); if (btnLoc) btnLoc.focus(); return; }
      } else if (where && where.value.trim() && where.value.trim() !== lastQuery && state.center.label !== (ui.place_you || 'you')) pre = findPlace();
      var run = ++state.run;
      var useRv = !!(opts && opts.reveal) && rvOpen(spec, run);
      state.busy = true;
      showError('');
      mark('state', 'working');
      var t0 = performance.now();
      setLoading(true);
      if (!useRv) showMap();
      pre.then(function (ok) {
        if (ok === false) throw Object.assign(new Error('nowhere'), { code: 'silent' });
        if (!state.center) throw Object.assign(new Error('nowhere'), { code: 'no_center' });
        return prepareData(true);
      }).then(function () {
        if (!state.dataset) throw Object.assign(new Error('nowhere'), { code: 'no_center' });
        if (useRv) { rvProgress(.35); rvKick(); }
        return host.fit(state.dataset.key, { spec: spec, targetM: miles() * engineMain.M_PER_MI, x: state.center.x, y: state.center.y, align: true, budgetMs: 3000, nearby: nearbyOn() ? { maxM: NEAR_M } : null }, function (phase, n) {
          if (!useRv || run !== rv.run) return;
          if (phase === 'nearby') { rvProgress(.8); if (n) { clearInterval(rv.lineTimer); RV.line.textContent = fill(ui.reveal_nearby || '', { place: shortPlace() }); } }
          else rvProgress(rv.prog >= .8 ? rv.prog + .03 : phase === 'prescore' ? .5 : .5 + .06 * n);
        });
      }).then(function (res) {
        if (run !== state.run) return;
        state.busy = false;
        setLoading(false);
        state.fits = res.fits; state.fitIdx = 0; state.spec = spec; state.meta = res;
        state.moved = res.moved || null; state.atPin = false;
        state.alt = res.moved && res.pinFits ? { fits: res.pinFits } : null;
        mark('moved', res.moved ? String(res.moved.d) : '');
        mark('ms', String(Math.round(performance.now() - t0)));
        mark('engine-ms', String(res.ms));
        mark('host', host.mode);
        mark('state', 'done');
        mark('error-code', '');
        showResult(true);
        if (useRv) rvResult(run);
      }).catch(function (e) {
        if (run !== state.run) return;
        if (useRv) rvClose();
        if (e && e.code === 'silent') { setLoading(false); state.busy = false; mark('state', 'error'); return; }
        handleError(e);
      });
    }
    form.addEventListener('submit', function (e) { e.preventDefault(); makeRoute({ reveal: true }); });
    if (errRetry) errRetry.addEventListener('click', function () { makeRoute({ reveal: true }); });

    /* on phones the button sits below the map: bring the map back into view */
    function showMap() {
      var r = mapWrap.getBoundingClientRect(), vh = window.innerHeight || 800;
      if (r.top >= 0 && r.bottom <= vh) return;
      var reduce = window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches;
      try { mapWrap.scrollIntoView({ block: r.height > vh ? 'start' : 'center', behavior: reduce ? 'auto' : 'smooth' }); } catch (e) { mapWrap.scrollIntoView(); }
    }
    function current() { return state.fits && state.fits[state.fitIdx]; }
    function connNote() { var sp = state.spec; return sp && sp.kind === 'shape' && !engineMain.GALLERY_TEXT[sp.id] ? (ui.connector_note_shape || pc.connector_note) : pc.connector_note; }
    function paintResult() {
      var f = current(); if (!f) return;
      var mi = f.lengthM / engineMain.M_PER_MI, pace = readPace() || 600;
      scoreEl.textContent = fill(pc.score_format || '{pct}% {shape}', { pct: f.match, shape: state.spec.scoreName });
      summaryEl.textContent = fill(pc.result_format, { miles: fmtMiles(mi), time: fmtTime(mi * pace, ui) });
      if (lowEl) lowEl.hidden = f.match >= 70;
      if (connEl) { connEl.hidden = !f.parts.some(function (p) { return p.c; }); connEl.textContent = connNote(); }
      if (fitN) fitN.textContent = state.fits.length > 1 ? fill(ui.fit_n || 'Fit {n} of {total}', { n: state.fitIdx + 1, total: state.fits.length }) : '';
      if (btnAnother) btnAnother.disabled = state.fits.length < 2;
      paintMoved(movedEl, movedText, moveBack);
      mark('miles', fmtMiles(mi));
      mark('match', String(f.match));
      mark('fit', String(state.fitIdx));
      canvas.setAttribute('aria-label', fill(ui.map_label || 'Map: your {shape} route, {miles} miles, drawn on the streets.', { shape: state.spec.label, miles: fmtMiles(mi) }));
      /* while the reveal plays, the card waits: it is drawn when the final screen shows (keeps the animation smooth on phones) */
      if (rv.open && rv.stage !== 'final') { state.previewDue = true; return; }
      drawPreview();
    }
    /* "Moved 0.8 mi north for a better fit." with Move it back (and back again) */
    function movedWords() {
      var m = state.moved; if (!m) return null;
      var dirs = ui.dirs || ['east', 'northeast', 'north', 'northwest', 'west', 'southwest', 'south', 'southeast'];
      var k = ((Math.round(Math.atan2(m.dy, m.dx) / (Math.PI / 4)) % 8) + 8) % 8;
      return { miles: fmtMiles(m.d / engineMain.M_PER_MI), dir: dirs[k] };
    }
    function paintMoved(box, textEl, btn) {
      if (!box) return;
      var w = movedWords();
      box.hidden = !w;
      if (!w) return;
      if (state.atPin) {
        textEl.textContent = fill(ui.moved_back_note || '', { miles: w.miles, dir: w.dir, pct: state.alt ? state.alt.fits[0].match : '' });
        btn.textContent = ui.move_again || 'Use the better spot';
      } else {
        textEl.textContent = fill(ui.moved_note || '', { miles: w.miles, dir: w.dir });
        btn.textContent = ui.move_back || 'Move it back';
      }
      btn.hidden = !state.alt;
    }
    function swapSpot() {
      if (!state.alt || !state.fits) return;
      var t = state.fits; state.fits = state.alt.fits; state.alt.fits = t;
      state.atPin = !state.atPin; state.stay = state.atPin; state.fitIdx = 0;
      mark('at-pin', state.atPin ? '1' : '0');
      state.story = null;
      showResult(false);
      if (rv.open && rv.stage === 'final') { rv.path = rvPath(current()); rvFinal(true); }
    }
    if (moveBack) moveBack.addEventListener('click', swapSpot);
    /* ---------- drag to fix a detour: handles along the route; a dropped handle reroutes the stretch between its
       neighbors through the nearest street (shortest path), then miles, match, card and GPX update ---------- */
    var btnEdit = $('[data-rt-ra-edit]'), btnUndo = $('[data-rt-ra-undo]'), editHint = $('[data-rt-ra-edit-hint]');
    function flatOf(f) {
      var o = { n: [], xy: [], ll: [], c: [] };
      f.parts.forEach(function (p, i) {
        for (var k = i ? 1 : 0; k < p.n.length; k++) {
          if (o.n.length) o.c.push(p.c ? 1 : 0);
          o.n.push(p.n[k]); o.xy.push(p.xy[2 * k], p.xy[2 * k + 1]); o.ll.push(p.ll[2 * k], p.ll[2 * k + 1]);
        }
      });
      return o;
    }
    function partsOf(o) {
      var parts = [], cur = null;
      for (var j = 0; j < o.c.length; j++) {
        if (!cur || cur.c !== o.c[j]) { cur = { c: o.c[j], n: [o.n[j]], xy: [o.xy[2 * j], o.xy[2 * j + 1]], ll: [o.ll[2 * j], o.ll[2 * j + 1]] }; parts.push(cur); }
        cur.n.push(o.n[j + 1]); cur.xy.push(o.xy[2 * j + 2], o.xy[2 * j + 3]); cur.ll.push(o.ll[2 * j + 2], o.ll[2 * j + 3]);
      }
      return parts.length ? parts : [{ c: 0, n: o.n.slice(), xy: o.xy.slice(), ll: o.ll.slice() }];
    }
    function makeHandles(o) {
      var cum = [0], j;
      for (j = 1; j < o.n.length; j++) cum.push(cum[j - 1] + Math.hypot(o.xy[2 * j] - o.xy[2 * j - 2], o.xy[2 * j + 1] - o.xy[2 * j - 1]));
      var total = cum[cum.length - 1], N = Math.max(6, Math.min(18, Math.round(total / 450))), hs = [];
      j = 0;
      for (var k = 1; k < N; k++) {
        var t = total * k / N;
        while (j < cum.length - 1 && cum[j] < t) j++;
        if (j > 0 && j < o.n.length - 1 && (!hs.length || hs[hs.length - 1] < j)) hs.push(j);
      }
      return hs;
    }
    function toWorld(e) {
      var r = canvas.getBoundingClientRect(), v = state.view;
      return [v.x + (e.clientX - r.left - r.width / 2) / v.s, v.y - (e.clientY - r.top - r.height / 2) / v.s];
    }
    function hitHandle(e) {
      if (!state.flat) return -1;
      var r = canvas.getBoundingClientRect(), tf = tfOf(state.view, r.width, r.height), best = -1, bd = 24;
      state.handles.forEach(function (j, k) { var p = tf(state.flat.xy[2 * j], state.flat.xy[2 * j + 1]), d = Math.hypot(p[0] - (e.clientX - r.left), p[1] - (e.clientY - r.top)); if (d < bd) { bd = d; best = k; } });
      return best;
    }
    function drawHandles(tf) {
      var o = state.flat, hs = state.handles, d = state.drag || state.key;
      if (d) {
        var a = d.k > 0 ? hs[d.k - 1] : 0, b = d.k < hs.length - 1 ? hs[d.k + 1] : o.n.length - 1, pa = tf(o.xy[2 * a], o.xy[2 * a + 1]), pb = tf(o.xy[2 * b], o.xy[2 * b + 1]), pm = tf(d.wx, d.wy);
        ctx.strokeStyle = '#E8590C'; ctx.lineWidth = 3; ctx.setLineDash([7, 6]);
        ctx.beginPath(); ctx.moveTo(pa[0], pa[1]); ctx.lineTo(pm[0], pm[1]); ctx.lineTo(pb[0], pb[1]); ctx.stroke(); ctx.setLineDash([]);
      }
      hs.forEach(function (j, k) {
        var p = d && d.k === k ? tf(d.wx, d.wy) : tf(o.xy[2 * j], o.xy[2 * j + 1]), on = k === state.sel;
        ctx.fillStyle = on ? COLORS.sun : '#fff'; ctx.strokeStyle = on ? '#E8590C' : COLORS.route; ctx.lineWidth = 3;
        ctx.beginPath(); ctx.arc(p[0], p[1], on ? 10 : 8, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
      });
    }
    function snapshot(f) { return { parts: f.parts, lengthM: f.lengthM, match: f.match, edited: f.edited }; }
    function setEdit(on) {
      state.edit = !!on && !!current();
      state.drag = null; state.key = null; state.sel = -1;
      if (state.edit) { state.flat = flatOf(current()); state.handles = makeHandles(state.flat); }
      else { state.flat = null; state.undo = []; }
      if (btnEdit) { btnEdit.setAttribute('aria-pressed', state.edit ? 'true' : 'false'); btnEdit.textContent = state.edit ? (ui.edit_done || 'Done') : (ui.edit_btn || 'Fix a detour'); }
      if (editHint) editHint.hidden = !state.edit;
      if (btnUndo) btnUndo.hidden = !state.edit || !state.undo.length;
      canvas.classList.toggle('is-editing', state.edit);
      mark('edit', state.edit ? '1' : '0');
      drawMap();
    }
    function applyDrag(k, wx, wy) {
      var f = current(), o = state.flat, hs = state.handles;
      if (!f || !o || state.editBusy) return;
      var a = k > 0 ? hs[k - 1] : 0, b = k < hs.length - 1 ? hs[k + 1] : o.n.length - 1, key = state.dataset.key, fitRef = f;
      state.editBusy = true; mark('edit-busy', '1');
      host.cmd('reroute', key, { a: o.n[a], b: o.n[b], x: wx, y: wy }).then(function (rr) {
        var no = {
          n: o.n.slice(0, a).concat(rr.n, o.n.slice(b + 1)),
          xy: o.xy.slice(0, 2 * a).concat(rr.xy, o.xy.slice(2 * b + 2)),
          ll: o.ll.slice(0, 2 * a).concat(rr.ll, o.ll.slice(2 * b + 2)),
          c: o.c.slice(0, a).concat(new Array(rr.n.length - 1).fill(0), o.c.slice(b))
        };
        return host.cmd('rescore', key, { nodes: no.n, c: no.c, ideal: f.ideal, h: f.h, twin: f.twin, isText: f.isText }).then(function (rs) {
          if (current() !== fitRef || !state.edit) return;
          state.undo.push({ snap: snapshot(f), flat: o, handles: hs.slice() });
          var delta = (rr.n.length - 1) - (b - a);
          state.handles = hs.map(function (j, i) { return i < k ? j : i === k ? a + rr.via : j + delta; });
          state.flat = no;
          f.parts = partsOf(no); f.lengthM = rs.lengthM; f.match = rs.match; f.edited = true;
          afterEdit();
          announce(fill(ui.edit_status || '', { miles: fmtMiles(f.lengthM / engineMain.M_PER_MI), pct: f.match, shape: state.spec.scoreName }));
        });
      }).catch(function () { say(ui.edit_fail || ''); drawMap(); }).then(function () { state.editBusy = false; mark('edit-busy', ''); });
    }
    function afterEdit() {
      state.edits = (state.edits || 0) + 1; state.story = null;
      mark('edits', String(state.edits));
      if (btnUndo) btnUndo.hidden = !state.undo.length;
      paintResult(); drawMap();
    }
    if (btnEdit) btnEdit.addEventListener('click', function () { setEdit(!state.edit); if (state.edit) { showMap(); try { canvas.focus({ preventScroll: true }); } catch (e) {} } });
    if (btnUndo) btnUndo.addEventListener('click', function () {
      var f = current(), u = state.undo.pop();
      if (!f || !u) return;
      f.parts = u.snap.parts; f.lengthM = u.snap.lengthM; f.match = u.snap.match; f.edited = u.snap.edited;
      state.flat = u.flat; state.handles = u.handles; state.sel = -1;
      afterEdit();
      if (!state.undo.length && btnEdit) btnEdit.focus();
    });
    /* keyboard: Page Up and Page Down pick a dot, arrows move it, Enter reroutes, Escape lets go */
    function editKey(e) {
      var k = e.key, hs = state.handles;
      if (!hs || !hs.length) return false;
      if (k === 'PageDown' || k === 'PageUp') {
        state.sel = state.sel < 0 ? 0 : (state.sel + (k === 'PageDown' ? 1 : -1) + hs.length) % hs.length;
        var j = hs[state.sel]; state.key = { k: state.sel, wx: state.flat.xy[2 * j], wy: state.flat.xy[2 * j + 1] };
        drawMap(); announce(fill(ui.edit_pick || 'Dot {n} of {total}', { n: state.sel + 1, total: hs.length })); return true;
      }
      if (!state.key) return false;
      var st = 40 / state.view.s;
      if (k === 'ArrowLeft') state.key.wx -= st; else if (k === 'ArrowRight') state.key.wx += st;
      else if (k === 'ArrowUp') state.key.wy += st; else if (k === 'ArrowDown') state.key.wy -= st;
      else if (k === 'Enter') { var d = state.key; state.key = null; applyDrag(d.k, d.wx, d.wy); return true; }
      else if (k === 'Escape') { state.key = null; state.sel = -1; drawMap(); return true; }
      else return false;
      drawMap(); return true;
    }
    function showResult(first) {
      setEdit(false);
      var f = current();
      result.hidden = false;
      var b = fitBounds(f);
      viewFit(b, .86);
      if (btnHere) btnHere.hidden = true;
      paintResult();
      if (rv.open) { state.mapDue = true; } else animateRoute();
      var mi = f.lengthM / engineMain.M_PER_MI;
      announce(fill(pc.score_format, { pct: f.match, shape: state.spec.scoreName }) + '. ' + fill(pc.result_format, { miles: fmtMiles(mi), time: fmtTime(mi * (readPace() || 600), ui) }));
      if (rv.open) return; /* the reveal has the stage; the editor result waits behind it */
      if (first && card) card.focus({ preventScroll: true });
      if (first) showMap();
    }
    if (btnAnother) btnAnother.addEventListener('click', function () {
      if (!state.fits || state.fits.length < 2) return;
      state.fitIdx = (state.fitIdx + 1) % state.fits.length;
      showResult(false);
    });
    if (btnShape) btnShape.addEventListener('click', function () {
      if (gallery.scrollIntoView) gallery.scrollIntoView({ block: 'center', behavior: 'smooth' });
      var r = radios().filter(function (x) { return x.checked; })[0] || radios()[0];
      if (r) r.focus({ preventScroll: true });
    });

    /* ---------- card and GPX ---------- */
    function cardData() {
      var f = current(), mi = f ? f.lengthM / engineMain.M_PER_MI : 0;
      return { kicker: pc.card_top || '', name: state.spec ? state.spec.label : '', miles: fill(pc.distance_format || '{miles} miles', { miles: fmtMiles(mi) }), streets: state.streets, fit: f, osm: pc.osm_credit };
    }
    function baseName() { var f = current(); return 'run-art-' + slug(state.spec ? state.spec.label : 'route') + '-' + fmtMiles(f ? f.lengthM / engineMain.M_PER_MI : 0).replace('.', '-') + '-mi'; }
    function cardKey() { return state.run + '|' + state.fitIdx + '|' + (state.atPin ? 1 : 0) + '|' + (state.edits || 0); }
    function drawPreview() {
      state.pending = null;
      if (!cardCanvas || !current()) return;
      var d = cardData();
      drawCard(cardCanvas.getContext('2d'), CARD_W, CARD_H, d);
      cardCanvas.setAttribute('aria-label', fill(ui.card_label || 'Art card preview: {name}, {miles}, runntell.com', { name: d.name, miles: d.miles }));
      var key = cardKey();
      canvasToBlob(cardCanvas).then(function (blob) {
        if (key !== cardKey()) return;
        try { state.pending = { key: key, file: new File([blob], baseName() + '.png', { type: 'image/png' }), blob: blob }; } catch (e) { state.pending = { key: key, file: null, blob: blob }; }
        mark('png', String(blob.size));
      }).catch(function () {});
    }
    function isTouch() { return (window.matchMedia && matchMedia('(pointer:coarse)').matches) || /iPad|iPhone|iPod|Android/.test(navigator.userAgent); }
    function canShareFile(f) { try { return !!(f && navigator.canShare && navigator.share && navigator.canShare({ files: [f] })); } catch (e) { return false; } }
    function download(blob, name, msg, sayFn) {
      var url = URL.createObjectURL(blob), a = document.createElement('a');
      a.href = url; a.download = name;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(function () { URL.revokeObjectURL(url); }, 60000);
      (sayFn || say)(msg);
    }
    function pageUrl() {
      var c = document.querySelector('link[rel="canonical"]'), u = (c && c.href) || location.href;
      return /^https?:/.test(u) ? u.split('#')[0] : (data.page_url || 'https://runntell.com/');
    }
    function caption() { var f = current(); return fill(data.share_caption || '', { shape: state.spec ? state.spec.scoreName : '', miles: fmtMiles(f ? f.lengthM / engineMain.M_PER_MI : 0) }); }
    function copyText(t) {
      try { if (navigator.clipboard && navigator.clipboard.writeText) return navigator.clipboard.writeText(t).then(function () { return true; }, function () { return false; }); } catch (e) {}
      return Promise.resolve(false);
    }
    function cardReady() { return state.pending && state.pending.key === cardKey() ? state.pending : null; }
    function cardBlob() {
      var r = cardReady();
      if (r) return Promise.resolve(r.blob);
      var c = document.createElement('canvas'); c.width = CARD_W; c.height = CARD_H; drawCard(c.getContext('2d'), CARD_W, CARD_H, cardData());
      return canvasToBlob(c);
    }
    /* launch metrics: Shopify customer events (read by a custom pixel), plus dataLayer if GA or GTM is added later */
    function track(name) {
      var f = current(), d = { shape: state.spec ? (state.spec.id === 'custom' ? 'text' : state.spec.id) : '', miles: f ? Math.round(f.lengthM / engineMain.M_PER_MI * 10) / 10 : 0 };
      try { if (window.Shopify && Shopify.analytics && Shopify.analytics.publish) Shopify.analytics.publish('run_art_' + name, d); } catch (e) {}
      try { if (window.dataLayer && window.dataLayer.push) window.dataLayer.push({ event: 'run_art_' + name, run_art_shape: d.shape, run_art_miles: d.miles }); } catch (e) {}
      mark('tracked', name);
    }
    function doGpx(sayFn) {
      var f = current(); if (!f) return;
      if (demo) { sayFn(ui.demo_download || ''); mark('save-path', 'demo'); return; }
      var mi = fmtMiles(f.lengthM / engineMain.M_PER_MI);
      var name = fill(ui.gpx_name || 'Run & Tell: {shape}, {miles} mi', { shape: state.spec.label, miles: mi });
      download(new Blob([buildGpx(f, name)], { type: 'application/gpx+xml' }), baseName() + '.gpx', ui.status_gpx || '', sayFn);
      mark('save-path', 'gpx');
      track('gpx_download');
    }
    function doTcx(sayFn) {
      var f = current(); if (!f) return;
      if (demo) { sayFn(ui.demo_download || ''); mark('save-path', 'demo'); return; }
      var name = fill(ui.gpx_name || 'Run & Tell: {shape}, {miles} mi', { shape: state.spec.label, miles: fmtMiles(f.lengthM / engineMain.M_PER_MI) });
      download(new Blob([buildTcx(f, name, readPace() || 600, state.spec.label + ' ' + fmtMiles(f.lengthM / engineMain.M_PER_MI) + ' mi')], { type: 'application/vnd.garmin.tcx+xml' }), baseName() + '.tcx', ui.status_tcx || '', sayFn);
      mark('save-path', 'tcx');
      track('tcx_download');
    }
    /* Save card: the share sheet on phones (Save Image lives there), a PNG download elsewhere */
    function doCard(sayFn, btn) {
      if (!current()) return;
      if (demo) { sayFn(ui.demo_download || ''); mark('save-path', 'demo'); return; }
      var ready = cardReady(), d = cardData();
      track('card_save');
      if (ready && ready.file && isTouch() && canShareFile(ready.file)) {
        mark('save-path', 'share');
        navigator.share({ files: [ready.file], title: d.name + ' ' + d.miles, text: caption() })
          .then(function () { sayFn(ui.status_shared || ''); })
          .catch(function (err) { if (err && err.name === 'AbortError') { sayFn(''); return; } download(ready.blob, baseName() + '.png', fill(ui.status_saved, { w: CARD_W, h: CARD_H }), sayFn); mark('save-path', 'download'); });
        return;
      }
      if (btn) btn.disabled = true;
      cardBlob().then(function (blob) { download(blob, baseName() + '.png', fill(ui.status_saved, { w: CARD_W, h: CARD_H }), sayFn); mark('save-path', 'download'); })
        .catch(function (e) { sayFn(ui.status_failed || ''); if (window.console) console.error('[rt-run-art]', e); })
        .then(function () { if (btn) btn.disabled = false; });
    }
    /* Share my run art: card + caption + link (texts, WhatsApp, anything in the share sheet) */
    function doShare(sayFn) {
      if (!current()) return;
      if (demo) { sayFn(ui.demo_share || ui.demo_download || ''); mark('share-path', 'demo'); return; }
      var ready = cardReady(), d = cardData(), text = caption(), url = pageUrl();
      track('share');
      function fallback() {
        mark('share-path', 'download');
        cardBlob().then(function (blob) {
          return copyText(text + ' ' + url).then(function (copied) {
            download(blob, baseName() + '.png', copied ? (ui.status_share_fallback || '') : fill(ui.status_saved, { w: CARD_W, h: CARD_H }), sayFn);
          });
        }).catch(function () { sayFn(ui.status_failed || ''); });
      }
      function failed(err) { if (err && err.name === 'AbortError') { sayFn(''); return; } fallback(); }
      if (ready && ready.file && canShareFile(ready.file)) {
        mark('share-path', 'files');
        navigator.share({ files: [ready.file], title: d.name + ' ' + d.miles, text: text + ' ' + url }).then(function () { sayFn(ui.status_shared || ''); }, failed);
        return;
      }
      if (navigator.share) {
        mark('share-path', 'link');
        navigator.share({ title: d.name + ' ' + d.miles, text: text, url: url }).then(function () { sayFn(ui.status_shared || ''); }, failed);
        return;
      }
      fallback();
    }
    /* Instagram Story: the 1080 x 1920 image through the share sheet (pick Instagram, then Stories).
       No Instagram deep link: those need a Facebook app ID. Elsewhere: download plus a one-line tip. */
    function storyData() { var d = cardData(), f = current(); d.stats = d.miles + ', ' + fill(pc.score_format || '{pct}% {shape}', { pct: f ? f.match : 0, shape: state.spec ? state.spec.scoreName : '' }); d.made = ui.story_made || ''; return d; }
    function makeStory(canvasEl) {
      var c = canvasEl || document.createElement('canvas');
      c.width = STORY_W; c.height = STORY_H;
      drawStory(c.getContext('2d'), storyData());
      var key = cardKey();
      return canvasToBlob(c).then(function (blob) {
        var file = null;
        try { file = new File([blob], baseName() + '-story.png', { type: 'image/png' }); } catch (e) {}
        if (key === cardKey()) state.story = { key: key, blob: blob, file: file };
        mark('story-png', String(blob.size));
        return state.story;
      });
    }
    function doStory(sayFn) {
      if (!current()) return;
      if (demo) { sayFn(ui.demo_story || ui.demo_download || ''); mark('story-path', 'demo'); return; }
      track('story');
      var st = state.story && state.story.key === cardKey() ? state.story : null;
      if (!st) { makeStory(RV && RV.story).then(function (s) { if (s) useStory(s, sayFn); else sayFn(ui.status_failed || ''); }, function () { sayFn(ui.status_failed || ''); }); return; }
      useStory(st, sayFn);
    }
    function useStory(st, sayFn) {
      function save() { download(st.blob, baseName() + '-story.png', ui.status_story_saved || '', sayFn); mark('story-path', 'download'); }
      if (st.file && isTouch() && canShareFile(st.file)) {
        mark('story-path', 'share');
        navigator.share({ files: [st.file] }).then(function () { sayFn(ui.status_shared || ''); }, function (err) { if (err && err.name === 'AbortError') { sayFn(''); return; } save(); });
        return;
      }
      save();
    }
    if (btnGpx) btnGpx.addEventListener('click', function () { doGpx(say); });
    var btnTcx = $('[data-rt-ra-tcx]');
    if (btnTcx) btnTcx.addEventListener('click', function () { doTcx(say); });
    if (btnCard) btnCard.addEventListener('click', function () {
      if (demo && cardCanvas && cardCanvas.scrollIntoView) cardCanvas.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      doCard(say, btnCard);
    });

    /* ---------- full-screen reveal: a short build-up, the route draws itself, then the result ---------- */
    var rvEl = $('[data-rt-ra-reveal]'), RV = null;
    if (rvEl) {
      var rq = function (k) { return rvEl.querySelector('[data-rt-ra-rv-' + k + ']'); };
      RV = { close: rq('close'), canvas: rq('canvas'), build: rq('build'), line: rq('line'), progress: rq('progress'), fill: rq('fill'), final: rq('final'), score: rq('score'),
        miles: rq('miles'), time: rq('time'), pace: rq('pace'), conn: rq('conn'), low: rq('low'), status: rq('status'), live: rq('live'), cardPrev: rq('card'), story: rq('story'),
        share: rq('share'), storyBtn: rq('story-btn'), save: rq('save'), gpx: rq('gpx'), tcx: rq('tcx'), shape: rq('shape'), spot: rq('spot'),
        moved: rq('moved'), movedText: rq('moved-text'), moveback: rq('moveback') };
      /* desktop (no file share sheet): the Story button downloads, so say that instead of the share sheet hint */
      (function () {
        var h = rvEl.querySelector('.rt-ra__rv-hint'), probe = null;
        try { probe = new File([''], 'x.png', { type: 'image/png' }); } catch (e) {}
        if (h && ui.story_hint_save && !(isTouch() && canShareFile(probe))) h.textContent = ui.story_hint_save;
      })();
    }
    var rv = { open: false, stage: '', t0: 0, raf: 0, timer: 0, lineTimer: 0, prog: 0, layer: null, layerKey: '', run: 0, trigger: null, spec: null, path: null, drawT0: 0, finT0: 0, confetti: null };
    var RV_MIN_MS = 1500, RV_DRAW_MS = 2600, RV_FX_MS = 2200;
    function reduced() { return !!(window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches); }
    function rvSay(m) { if (RV && RV.status) RV.status.textContent = m || ''; }
    function shapeWord(spec) { return spec.kind === 'text' ? spec.text : spec.scoreName; }
    function shortPlace() {
      var l = state.center && state.center.label;
      if (!l || l === (ui.place_you || 'your location')) return ui.reveal_place_you || 'your neighborhood';
      return String(l).split(',')[0].trim();
    }
    function rvProgress(p) {
      rv.prog = Math.max(rv.prog, Math.min(1, p));
      if (RV.fill) RV.fill.style.width = (rv.prog * 100).toFixed(1) + '%';
      if (RV.progress) RV.progress.setAttribute('aria-valuenow', String(Math.round(rv.prog * 100)));
    }
    function rvOpen(spec, run) {
      if (!RV || !RV.canvas) return false;
      cancelAnimationFrame(rv.raf); clearInterval(rv.lineTimer); clearTimeout(rv.timer);
      if (!rv.open) rv.trigger = document.activeElement;
      rv.open = true; rv.stage = 'build'; rv.t0 = performance.now(); rv.prog = 0; rv.run = run; rv.spec = spec; rv.path = null; rv.confetti = null;
      rvEl.hidden = false;
      document.documentElement.classList.add('rt-ra-lock');
      RV.build.hidden = false; RV.final.hidden = true; RV.progress.hidden = false; rvSay('');
      if (RV.fill) RV.fill.style.width = '0%';
      rvProgress(.04);
      var lines = (ui.reveal_lines || []).map(function (l) { return fill(l, { shape: shapeWord(spec), place: shortPlace() }); }), i = 0;
      RV.line.textContent = lines[0] || '';
      rv.lineTimer = setInterval(function () {
        /* while the street data loads the bar keeps creeping (it never claims more than a third), and a slow server gets an honest line */
        if (rv.stage === 'build' && rv.prog < .34) rvProgress(rv.prog + (.32 - rv.prog) * .08);
        if (rv.stage === 'build' && rv.prog < .34 && performance.now() - rv.t0 > 12000) { RV.line.textContent = ui.reveal_slow || 'The map servers are busy right now. Still on it...'; return; }
        i = (i + 1) % Math.max(1, lines.length); RV.line.textContent = lines[i] || '';
      }, 1100);
      mark('reveal', 'build');
      RV.close.focus();
      rvKick();
      return true;
    }
    function rvClose(focusEl) {
      if (!rv.open) return;
      rv.open = false; rv.stage = '';
      cancelAnimationFrame(rv.raf); clearInterval(rv.lineTimer); clearTimeout(rv.timer);
      if (state.previewDue) { state.previewDue = false; if (current()) drawPreview(); }
      if (state.mapDue) { state.mapDue = false; drawMap(); }
      rvEl.hidden = true;
      document.documentElement.classList.remove('rt-ra-lock');
      mark('reveal', 'closed');
      if (focusEl) { try { focusEl.focus({ preventScroll: true }); } catch (e) { focusEl.focus(); } return; }
      /* back to the editor: the map (and the result, or an error) in view */
      showMap();
      var to = !result.hidden && card ? card : form.querySelector('[type=submit]');
      if (to && to.focus) { try { to.focus({ preventScroll: true }); } catch (e) { to.focus(); } }
    }
    function rvSize() {
      var c = RV.canvas, r = c.getBoundingClientRect(), dpr = Math.min(window.devicePixelRatio || 1, 2);
      var w = Math.max(1, Math.round(r.width * dpr)), h = Math.max(1, Math.round(r.height * dpr));
      if (c.width !== w || c.height !== h) { c.width = w; c.height = h; }
      return { w: Math.max(1, r.width), h: Math.max(1, r.height), dpr: dpr };
    }
    function rvView(R) {
      var f = rv.stage !== 'build' ? current() : null, b;
      if (f) b = fitBounds(f);
      else if (state.center && state.center.x != null && state.dataset) {
        var r = 1500; try { r = engineMain.plan(rv.spec, miles() * engineMain.M_PER_MI).half; } catch (e) {}
        b = [state.center.x - r, state.center.y - r, state.center.x + r, state.center.y + r];
      } else return null;
      var bw = Math.max(b[2] - b[0], 50), bh = Math.max(b[3] - b[1], 50);
      return { x: (b[0] + b[2]) / 2, y: (b[1] + b[3]) / 2, s: Math.min(R.w / bw, R.h / bh) * (f ? .84 : 1) };
    }
    function rvLayer(R, V) {
      /* streets are drawn once into an offscreen layer; each frame only copies it */
      var key = [RV.canvas.width, RV.canvas.height, V ? [V.x.toFixed(1), V.y.toFixed(1), V.s.toFixed(5)].join(',') : '-', state.dataset ? state.dataset.key : ''].join('|');
      if (rv.layer && rv.layerKey === key) return rv.layer;
      var c = rv.layer || document.createElement('canvas');
      c.width = RV.canvas.width; c.height = RV.canvas.height;
      var g = c.getContext('2d');
      g.setTransform(R.dpr, 0, 0, R.dpr, 0, 0);
      g.fillStyle = COLORS.bg; g.fillRect(0, 0, R.w, R.h);
      if (V && state.streets) {
        g.lineCap = 'round'; g.lineJoin = 'round';
        g.strokeStyle = 'rgba(255,249,236,0.22)'; g.lineWidth = V.s > .6 ? 1.6 : 1.1;
        var hw = R.w / 2 / V.s, hh = R.h / 2 / V.s;
        strokeStreets(g, state.streets, tfOf(V, R.w, R.h), [V.x - hw - 100, V.y - hh - 100, V.x + hw + 100, V.y + hh + 100]);
      }
      rv.layer = c; rv.layerKey = key;
      return c;
    }
    function rvPath(f) {
      var segs = [], total = 0;
      f.parts.forEach(function (p) {
        var xy = p.xy;
        for (var i = 2; i < xy.length; i += 2) {
          var l = Math.sqrt((xy[i] - xy[i - 2]) * (xy[i] - xy[i - 2]) + (xy[i + 1] - xy[i - 1]) * (xy[i + 1] - xy[i - 1]));
          segs.push({ c: !!p.c, ax: xy[i - 2], ay: xy[i - 1], bx: xy[i], by: xy[i + 1], s: total, l: l });
          total += l;
        }
      });
      return { segs: segs, total: total };
    }
    function rvRoute(g, tf, path, upto, glow) {
      var head = null, cur = '';
      g.lineCap = 'round'; g.lineJoin = 'round';
      if (glow) { g.shadowColor = COLORS.sun; g.shadowBlur = glow; }
      function flush() { if (cur) { g.stroke(); g.setLineDash([]); cur = ''; } }
      for (var i = 0; i < path.segs.length; i++) {
        var sg = path.segs[i];
        if (sg.s >= upto) break;
        var t = Math.min(1, (upto - sg.s) / (sg.l || 1)), a = tf(sg.ax, sg.ay), b = tf(sg.ax + (sg.bx - sg.ax) * t, sg.ay + (sg.by - sg.ay) * t);
        var kind = sg.c ? 'c' : 's';
        if (cur !== kind) {
          flush(); cur = kind;
          g.beginPath();
          g.strokeStyle = sg.c ? 'rgba(255,249,236,0.45)' : COLORS.ink;
          g.lineWidth = sg.c ? 2.5 : 5;
          g.setLineDash(sg.c ? [3, 7] : []);
          g.moveTo(a[0], a[1]);
        }
        g.lineTo(b[0], b[1]);
        head = b;
      }
      flush();
      g.shadowBlur = 0; g.shadowColor = 'transparent';
      return head;
    }
    function rvDot(g, p, r) {
      g.fillStyle = 'rgba(255,225,53,0.35)'; g.beginPath(); g.arc(p[0], p[1], r * 2.1, 0, Math.PI * 2); g.fill();
      g.fillStyle = COLORS.sun; g.strokeStyle = COLORS.bg; g.lineWidth = 2.5;
      g.beginPath(); g.arc(p[0], p[1], r, 0, Math.PI * 2); g.fill(); g.stroke();
    }
    function rvConfetti(R) {
      var cols = [COLORS.sun, COLORS.ink, '#FF8A65', '#8FE3C0', '#B9B6FF'], out = [];
      for (var i = 0; i < 110; i++) {
        var a = -Math.PI / 2 + (Math.random() - .5) * 2.2, v = (.55 + Math.random() * .7) * R.h;
        out.push({ x: R.w / 2 + (Math.random() - .5) * R.w * .3, y: R.h * .62, vx: Math.cos(a) * v * .8, vy: Math.sin(a) * v * 1.25, r: Math.random() * 6.3, vr: (Math.random() - .5) * 12, w: 6 + Math.random() * 6, h: 4 + Math.random() * 4, c: cols[i % cols.length] });
      }
      return out;
    }
    function rvRender(now) {
      var R = rvSize(), V = rvView(R), g = RV.canvas.getContext('2d');
      g.setTransform(1, 0, 0, 1, 0, 0);
      g.drawImage(rvLayer(R, V), 0, 0);
      g.setTransform(R.dpr, 0, 0, R.dpr, 0, 0);
      var more = false;
      if (rv.stage === 'build') {
        if (V && state.center && !reduced()) {
          var tf0 = tfOf(V, R.w, R.h), c0 = tf0(state.center.x, state.center.y), ph = ((now - rv.t0) % 1400) / 1400;
          g.strokeStyle = 'rgba(255,225,53,' + (0.7 * (1 - ph)).toFixed(3) + ')'; g.lineWidth = 3;
          g.beginPath(); g.arc(c0[0], c0[1], 10 + ph * Math.min(R.w, R.h) * .38, 0, Math.PI * 2); g.stroke();
        }
        if (V && state.center) { var c1 = tfOf(V, R.w, R.h)(state.center.x, state.center.y); rvDot(g, c1, 7); }
        more = !reduced();
      } else if (rv.path) {
        var tf = tfOf(V, R.w, R.h), f = current();
        var st = tf(f.parts[0].xy[0], f.parts[0].xy[1]);
        if (rv.stage === 'draw') {
          var p = Math.min(1, (now - rv.drawT0) / RV_DRAW_MS), e = p < .5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2;
          var head = rvRoute(g, tf, rv.path, e * rv.path.total, 0);
          rvDot(g, st, 5);
          if (head) rvDot(g, head, 8);
          if (p >= 1) rvFinal(); else more = true;
        } else {
          var k = reduced() ? 1 : Math.min(1, (now - rv.finT0) / RV_FX_MS);
          var glow = reduced() ? 10 : 10 + 22 * Math.sin(Math.min(1, k * 2.2) * Math.PI);
          rvRoute(g, tf, rv.path, Infinity, glow);
          rvDot(g, st, 7);
          if (rv.confetti && k < 1) {
            var tt = (now - rv.finT0) / 1000;
            rv.confetti.forEach(function (c) {
              var x = c.x + c.vx * tt * .5, y = c.y + c.vy * tt * .5 + .5 * R.h * 1.6 * tt * tt;
              if (y > R.h + 20) return;
              g.save(); g.translate(x, y); g.rotate(c.r + c.vr * tt); g.fillStyle = c.c; g.fillRect(-c.w / 2, -c.h / 2, c.w, c.h); g.restore();
            });
          }
          more = k < 1;
        }
      }
      return more;
    }
    function rvKick() {
      cancelAnimationFrame(rv.raf);
      (function frame(now) { if (!rv.open) return; if (rvRender(now || performance.now())) rv.raf = requestAnimationFrame(frame); })(performance.now());
    }
    function rvResult(run) {
      if (!rv.open || run !== rv.run) return;
      rvProgress(1);
      var wait = Math.max(0, RV_MIN_MS - (performance.now() - rv.t0)); /* a short moment, never a fake long wait */
      rv.timer = setTimeout(function () {
        if (!rv.open || run !== rv.run || !current()) return;
        clearInterval(rv.lineTimer);
        rv.stage = 'draw';
        rv.path = rvPath(current());
        rv.drawT0 = performance.now();
        RV.progress.hidden = true;
        RV.line.textContent = fill(ui.reveal_drawing || '', { shape: shapeWord(rv.spec) });
        mark('reveal', 'draw');
        if (reduced()) rvFinal();
        else rvKick();
      }, wait);
    }
    function rvFinal(quiet) {
      rv.stage = 'final';
      rv.finT0 = quiet ? performance.now() - RV_FX_MS : performance.now();
      var f = current(), mi = f.lengthM / engineMain.M_PER_MI, pace = readPace() || 600;
      RV.score.textContent = fill(pc.score_format || '{pct}% {shape}', { pct: f.match, shape: state.spec.scoreName });
      RV.miles.textContent = fmtMiles(mi);
      RV.time.textContent = fmtTime(mi * pace, ui);
      RV.pace.textContent = fill(ui.stat_pace || 'at {pace} per mile', { pace: Math.floor(pace / 60) + ':' + ('0' + pace % 60).slice(-2) });
      if (RV.conn) { RV.conn.hidden = !f.parts.some(function (p) { return p.c; }); RV.conn.textContent = connNote(); }
      if (RV.low) RV.low.hidden = f.match >= 70;
      paintMoved(RV.moved, RV.movedText, RV.moveback);
      if (state.previewDue || !state.pending || state.pending.key !== cardKey()) { state.previewDue = false; drawPreview(); }
      if (RV.cardPrev && cardCanvas) { var cg = RV.cardPrev.getContext('2d'); cg.clearRect(0, 0, RV.cardPrev.width, RV.cardPrev.height); cg.drawImage(cardCanvas, 0, 0, RV.cardPrev.width, RV.cardPrev.height); }
      RV.cardPrev && RV.cardPrev.setAttribute('aria-label', cardCanvas ? cardCanvas.getAttribute('aria-label') : '');
      state.story = null;
      var runNow = rv.run;
      /* the story image is big: make it once the confetti is done (or now, without motion) */
      rv.storyTimer = setTimeout(function () { if (rv.run === runNow && current()) makeStory(RV.story).catch(function () {}); }, reduced() ? 0 : RV_FX_MS + 100);
      RV.build.hidden = true;
      RV.final.hidden = false;
      rvSay('');
      if (RV.live) RV.live.textContent = RV.score.textContent + '. ' + fill(pc.result_format, { miles: fmtMiles(mi), time: fmtTime(mi * pace, ui) });
      rv.confetti = reduced() || quiet ? null : rvConfetti(rvSize());
      mark('reveal', 'final');
      rvKick();
    }
    if (rvEl) {
      RV.close.addEventListener('click', function () { rvClose(); });
      rvEl.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') { e.preventDefault(); rvClose(); return; }
        if (e.key !== 'Tab') return;
        var f = Array.prototype.filter.call(rvEl.querySelectorAll('button, [href], [tabindex]:not([tabindex="-1"])'), function (x) { return !x.disabled && x.offsetParent !== null; });
        if (!f.length) return;
        var first = f[0], last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      });
      if (RV.share) RV.share.addEventListener('click', function () { doShare(rvSay); });
      if (RV.storyBtn) RV.storyBtn.addEventListener('click', function () { doStory(rvSay); });
      if (RV.save) RV.save.addEventListener('click', function () { doCard(rvSay, RV.save); });
      if (RV.gpx) RV.gpx.addEventListener('click', function () { doGpx(rvSay); });
      if (RV.moveback) RV.moveback.addEventListener('click', swapSpot);
      if (RV.tcx) RV.tcx.addEventListener('click', function () { doTcx(rvSay); });
      if (RV.shape) RV.shape.addEventListener('click', function () {
        var t = selectedTabEl() || radios().filter(function (x) { return x.checked; })[0] || custom;
        rvClose(t);
        if (gallery.scrollIntoView) gallery.scrollIntoView({ block: 'center' });
      });
      if (RV.spot) RV.spot.addEventListener('click', function () {
        rvClose(where || btnLoc);
        if (where) { where.scrollIntoView({ block: 'center' }); where.select(); }
      });
      window.addEventListener('resize', function () { if (rv.open) rvKick(); });
    }

    /* fonts for the card. The font stylesheet loads without blocking the page, so wait for it first. */
    function loadCardFonts() {
      var loads = (document.fonts && document.fonts.load) ? [FONTS.big, FONTS.hand].map(function (f) { return document.fonts.load(fontStr(f, 48), 'Turkey 5.2 miles runntell.com').catch(function () {}); }) : [];
      withTimeout(Promise.all(loads), FONT_TIMEOUT).then(function () { state.fontsReady = true; mark('ready', '1'); if (current()) drawPreview(); });
    }
    var fontCss = document.querySelector('link[data-rt-ra-fontcss]');
    if (fontCss && !fontCss.hasAttribute('data-loaded')) {
      var fontCssDone = false;
      var onFontCss = function () { if (fontCssDone) return; fontCssDone = true; loadCardFonts(); };
      fontCss.addEventListener('load', onFontCss); fontCss.addEventListener('error', onFontCss);
      setTimeout(onFontCss, FONT_TIMEOUT);
    } else loadCardFonts();
    /* start the route engine Worker when the browser is idle (or on first use), not during page load */
    (window.requestIdleCallback ? function (f) { window.requestIdleCallback(f, { timeout: 3000 }); } : function (f) { setTimeout(f, 1500); })(function () {
      host.ready.then(function () { mark('host', host.mode); });
    });
    drawMap();
    root.__rtRaApi = { state: state, rv: rv, engine: engineMain, drawStory: drawStory, host: host, buildGpx: buildGpx, drawCard: drawCard, drawMap: drawMap, overpassQuery: overpassQuery };
  }

  function boot() { Array.prototype.forEach.call(document.querySelectorAll('[data-rt-ra]'), init); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  document.addEventListener('shopify:section:load', boot);
  window.RtRunArt = { engine: RtRunArtEngine, overpassQuery: overpassQuery, buildGpx: buildGpx, buildTcx: buildTcx, drawCard: drawCard, CARD_W: CARD_W, CARD_H: CARD_H };
})();
